import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  AccountBookOutlined,
  AlertOutlined,
  BankOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HeartOutlined,
  LogoutOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AdminNotificationBell from "../../components/AdminNotificationBell";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import {
  activateUser,
  approveSignupRequest,
  createSystemConfig,
  createUser,
  getAuditLogs,
  getBillingRecords,
  getSignupRequests,
  getSystemConfig,
  getUserList,
  reassignUserOrg,
  rejectSignupRequest,
  resetUserPassword,
  suspendUser,
  updateSystemConfig,
} from "../../services/adminService";
import {
  createTicket,
  getAllTickets,
  respondToTicket,
} from "../../services/supportService";
import {
  createFeeConfiguration,
  generateInvoices,
  getFeeConfigurations,
  getMonthlyInvoices,
  getUsageSummary,
  updateFeeConfiguration,
  updateInvoiceStatus,
} from "../../services/usageBillingService";
import {
  createOrg,
  deactivateOrg,
  generateOrgInvoice,
  getOrgBilling,
  getOrgBillingSummary,
  getOrgStats,
  listOrgInvoices,
  listOrgs,
  markInvoicePaid,
  updateOrg,
} from "../../services/operationsService";
import {
  createPickupPartner,
  getPickupPartnerBilling,
  listPickupPartners,
  updatePickupPartner,
} from "../../services/partnerOrgService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const toArray = (data) =>
  Array.isArray(data) ? data : (data?.results ?? []);
const toCount = (data) =>
  Array.isArray(data) ? data.length : (data?.count ?? data?.results?.length ?? 0);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.getFullYear()===n.getFullYear() && d.getMonth()===n.getMonth() && d.getDate()===n.getDate();
};

const DHN_ROLES = [
  "DOCTOR","PATIENT","PHARMACIST","OPHTHALMOLOGIST","OPTICIAN",
  "EMS_DISPATCHER","EMS_DRIVER","BILLING_STAFF","SYSTEM_ADMIN","OPERATIONS_MANAGER",
];

const BILLING_STATUS_COLOR = {
  DRAFT:"default", SUBMITTED:"blue", APPROVED:"green", REJECTED:"red", PAID:"cyan",
};
const TICKET_STATUS_COLOR = {
  OPEN:"orange", IN_PROGRESS:"blue", RESOLVED:"green", CLOSED:"default", ESCALATED:"red",
};
const PRIORITY_COLOR = {
  LOW:"default", MEDIUM:"blue", HIGH:"orange", CRITICAL:"red",
};

const MENU_ITEMS = [
  { key:"dashboard",       icon:<DashboardOutlined />,      label:"Dashboard" },
  { key:"signup-requests", icon:<SolutionOutlined />,       label:"Signup Requests" },
  { key:"users",           icon:<TeamOutlined />,            label:"Users" },
  { key:"mchusa-org",      icon:<CrownOutlined />,           label:"MCHUSA Organisation" },
  { key:"audit-logs",      icon:<FileSearchOutlined />,      label:"Audit Logs" },
  { key:"support-tickets", icon:<CustomerServiceOutlined />, label:"Support Tickets" },
  { key:"config",          icon:<SettingOutlined />,         label:"System Config" },
  { key:"usage-billing",   icon:<BarChartOutlined />,        label:"Usage & Billing" },
  { key:"partner-orgs",    icon:<BankOutlined />,            label:"Partner Organisations" },
  { key:"pickup-partners", icon:<HeartOutlined />,           label:"Pickup Partners" },
  { key:"profile",         icon:<UserOutlined />,            label:"My Profile" },
];

const INVOICE_STATUS_COLOR = {
  DRAFT:"default", SENT:"blue", PAID:"green", OVERDUE:"red",
};
const FEE_TYPE_LABEL = {
  PATIENT_REGISTRATION:     "Patient Registration",
  PRESCRIPTION_ISSUED:      "Prescription Issued",
  PRESCRIPTION_DISPENSED:   "Prescription Dispensed",
  EYE_EXAMINATION:          "Eye Examination",
  LENS_PRESCRIPTION_ISSUED: "Lens Prescription Issued",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  // data
  const [stats,       setStats]       = useState({ totalUsers:0, openTickets:0, auditEventsToday:0 });
  const [activityRows,setActivityRows]= useState([]);
  const [userRows,    setUserRows]    = useState([]);
  const [auditRows,   setAuditRows]   = useState([]);
  const [ticketRows,  setTicketRows]  = useState([]);
  const [configRows,  setConfigRows]  = useState([]);
  const [billingRows, setBillingRows] = useState([]); // eslint-disable-line no-unused-vars

  // signup requests
  const [signupRows,        setSignupRows]        = useState([]);
  const [signupLoading,     setSignupLoading]     = useState(false);
  const [signupRejectModal, setSignupRejectModal] = useState(null);
  const [rejectReason,      setRejectReason]      = useState("");
  const [approveModal,      setApproveModal]      = useState(null);
  const [approveOrg,        setApproveOrg]        = useState(null);

  // user management
  const [userStatusFilter, setUserStatusFilter] = useState("all"); // "all" | "active" | "suspended"
  const [userOrgFilter,    setUserOrgFilter]    = useState("all"); // "all" | org UUID
  const [userSearch,       setUserSearch]       = useState("");
  const [userRoleFilter,   setUserRoleFilter]   = useState("");
  const [userListLoading,  setUserListLoading]  = useState(false);
  const userSearchDebounceRef = useRef(null);

  // reassign org modal
  const [reassignOrgOpen,   setReassignOrgOpen]   = useState(false);
  const [reassignOrgTarget, setReassignOrgTarget] = useState(null);
  const [reassignOrgId,     setReassignOrgId]     = useState(null);

  // reset password modal
  const [resetPwTarget,   setResetPwTarget]   = useState(null);
  const [resetPwResult,   setResetPwResult]   = useState(null);
  const [resetPwLoading,  setResetPwLoading]  = useState(false);
  const [resetPwMode,     setResetPwMode]     = useState("generate"); // "generate" | "specific"
  const [resetPwSpecific, setResetPwSpecific] = useState("");
  const [resetPwConfirm,  setResetPwConfirm]  = useState("");

  // audit log search state
  const [auditFilters,      setAuditFilters]      = useState({});
  const [auditRefreshing,   setAuditRefreshing]   = useState(false);
  const [auditLastRefreshed,setAuditLastRefreshed]= useState(null);
  const [auditFilterForm]                         = Form.useForm();

  // support ticket respond state
  const [respondTarget,     setRespondTarget]     = useState(null);
  const [respondOpen,       setRespondOpen]       = useState(false);
  const [ticketDetail,      setTicketDetail]      = useState(null);
  const [ticketSourceFilter,setTicketSourceFilter]= useState("ALL");
  const [respondForm]                             = Form.useForm();

  // modals
  const [addUserOpen,    setAddUserOpen]    = useState(false);
  const [newTicketOpen,  setNewTicketOpen]  = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [addUserForm]    = Form.useForm();
  const [newTicketForm]  = Form.useForm();

  // config modal
  const [configModalOpen,   setConfigModalOpen]   = useState(false);
  const [configEditTarget,  setConfigEditTarget]  = useState(null); // null = add, object = edit
  const [configForm]                              = Form.useForm();

  // usage billing
  const [feeRows,          setFeeRows]          = useState([]);
  const [invoiceRows,      setInvoiceRows]       = useState([]);
  const [summaryRows,      setSummaryRows]       = useState([]);
  const [usageBillingLoaded, setUsageBillingLoaded] = useState(false);
  const [ubLoading,        setUbLoading]         = useState(false);
  const [generateResult,   setGenerateResult]    = useState(null);
  const [generateMonth,    setGenerateMonth]     = useState(null);
  const [editFeeOpen,      setEditFeeOpen]       = useState(false);
  const [editFeeTarget,    setEditFeeTarget]     = useState(null);
  const [addFeeOpen,       setAddFeeOpen]        = useState(false);
  const [invoiceDetailTarget, setInvoiceDetailTarget] = useState(null);
  const [editFeeForm]  = Form.useForm();
  const [addFeeForm]   = Form.useForm();

  // partner organisations
  const [orgRows,            setOrgRows]            = useState([]);
  const [orgLoaded,          setOrgLoaded]          = useState(false);
  const [orgLoading,         setOrgLoading]         = useState(false);
  const [orgCreateOpen,      setOrgCreateOpen]      = useState(false);
  const [orgCreateResult,    setOrgCreateResult]    = useState(null);
  const [orgDetailTarget,    setOrgDetailTarget]    = useState(null);
  const [orgDetailStats,     setOrgDetailStats]     = useState(null);
  const [orgDetailLoading,   setOrgDetailLoading]   = useState(false);
  const [orgEditOpen,        setOrgEditOpen]        = useState(false);
  const [orgEditTarget,      setOrgEditTarget]      = useState(null);
  const [orgBillingData,     setOrgBillingData]     = useState(null);
  const [orgBillingLoading,  setOrgBillingLoading]  = useState(false);
  const [orgBillingMonth,    setOrgBillingMonth]    = useState(null);
  const [orgBillingSummary,  setOrgBillingSummary]  = useState([]);
  const [orgInvoices,        setOrgInvoices]        = useState([]);
  const [orgInvoicesLoading, setOrgInvoicesLoading] = useState(false);
  const [invoiceGenLoading,  setInvoiceGenLoading]  = useState(false);
  const [invoiceDetailOpen,  setInvoiceDetailOpen]  = useState(false);
  const [partnerInvDetail,   setPartnerInvDetail]   = useState(null);
  const [orgModulesSaving,   setOrgModulesSaving]   = useState(false);
  const [orgCreateForm]      = Form.useForm();
  const [orgEditForm]        = Form.useForm();

  // pickup partners (PartnerOrganisation model — TunzaCare-type)
  const [ppRows,            setPpRows]            = useState([]);
  const [ppLoaded,          setPpLoaded]          = useState(false);
  const [ppLoading,         setPpLoading]         = useState(false);
  const [ppCreateOpen,      setPpCreateOpen]      = useState(false);
  const [ppCreateResult,    setPpCreateResult]    = useState(null);
  const [ppDetailTarget,    setPpDetailTarget]    = useState(null);
  const [ppDetailBilling,   setPpDetailBilling]   = useState(null);
  const [ppDetailLoading,   setPpDetailLoading]   = useState(false);
  const [ppEditOpen,        setPpEditOpen]        = useState(false);
  const [ppEditTarget,      setPpEditTarget]      = useState(null);
  const [ppSubmitting,      setPpSubmitting]      = useState(false);
  const [ppCreateForm]      = Form.useForm();
  const [ppEditForm]        = Form.useForm();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const todayStart = new Date(); todayStart.setHours(0,0,0,0);

        // Use allSettled so one failed call doesn't kill the entire dashboard load.
        const [usersResult, auditTodayResult, openTicketsResult, auditAllResult,
               ticketsAllResult, configResult, billingResult] =
          await Promise.allSettled([
            getUserList(),
            getAuditLogs({ start: todayStart.toISOString() }),
            getAllTickets({ status:"OPEN" }),
            getAuditLogs(),
            getAllTickets(),
            getSystemConfig(),
            getBillingRecords(),
          ]);

        if (cancelled) return;

        const ok  = (r) => r.status === "fulfilled" ? r.value : null;
        const usersRes       = ok(usersResult);
        const auditTodayRes  = ok(auditTodayResult);
        const openTicketsRes = ok(openTicketsResult);
        const auditAllRes    = ok(auditAllResult);
        const ticketsAllRes  = ok(ticketsAllResult);
        const configRes      = ok(configResult);
        const billingRes     = ok(billingResult);

        setStats({
          totalUsers:       usersRes       ? toCount(usersRes.data)       : 0,
          auditEventsToday: auditTodayRes  ? toCount(auditTodayRes.data)  : 0,
          openTickets:      openTicketsRes ? toCount(openTicketsRes.data)  : 0,
        });
        if (auditTodayRes) setActivityRows(toArray(auditTodayRes.data).slice(0,20).map(r=>({...r,key:r.id})));
        if (usersRes)      setUserRows(toArray(usersRes.data).map(r=>({...r,key:r.id})));
        if (auditAllRes)   setAuditRows(toArray(auditAllRes.data).map(r=>({...r,key:r.id})));
        if (ticketsAllRes) setTicketRows(toArray(ticketsAllRes.data).map(r=>({...r,key:r.id})));
        if (configRes)     setConfigRows(toArray(configRes.data).map(r=>({...r,_rowKey:r.id})));
        if (billingRes)    setBillingRows(toArray(billingRes.data).map(r=>({...r,key:r.id})));

        // If all critical calls failed, surface a warning
        if (!usersRes && !ticketsAllRes && !configRes) {
          setError("Dashboard data failed to load. Please retry.");
        }
      } catch (e) {
        if (!cancelled) setError("Dashboard data failed to load. Please retry.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load (and always-refresh) support tickets when user navigates to the tab.
  // This ensures fresh data even if the initial Promise.allSettled had a partial failure.
  const [ticketsLoading, setTicketsLoading] = useState(false);
  useEffect(() => {
    if (selectedMenu !== "support-tickets") return;
    setTicketsLoading(true);
    getAllTickets()
      .then(res => setTicketRows(toArray(res.data).map(r => ({ ...r, key: r.id }))))
      .catch(() => { /* ticketRows keeps whatever the initial load set */ })
      .finally(() => setTicketsLoading(false));
  }, [selectedMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load signup requests when user navigates to the tab.
  useEffect(() => {
    if (selectedMenu !== "signup-requests") return;
    fetchSignupRequests();
  }, [selectedMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers ────────────────────────────────────────────────────────────────

  // Tracks selected role in Add User form so pharmacy fields show/hide
  const [addUserRole, setAddUserRole] = useState("");

  const handleAddUser = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        email:      values.email,
        first_name: values.first_name,
        last_name:  values.last_name,
        role:       values.role,
        password:   values.password,
        is_active:  values.is_active ?? true,
      };
      // Include pharmacy fields when creating a pharmacist
      if (values.role === "PHARMACIST") {
        payload.pharmacy_name    = values.pharmacy_name;
        payload.license_number   = values.license_number;
        payload.pharmacy_address = values.pharmacy_address ?? "";
        payload.pharmacy_phone   = values.pharmacy_phone   ?? "";
        payload.is_verified      = values.pharmacy_is_verified ? "true" : "false";
      }
      const res = await createUser(payload);
      const isPharmacist = values.role === "PHARMACIST";
      message.success(
        isPharmacist
          ? `Pharmacist created. Pharmacy profile "${values.pharmacy_name}" created automatically.`
          : "User created successfully."
      );
      if (isPharmacist && res.data?.pharmacy_profile_created) {
        notification.success({
          message: "Pharmacist & Pharmacy Profile Created",
          description: `${values.email} has been registered and their pharmacy profile "${values.pharmacy_name}" has been created.`,
          duration: 6,
        });
      }
      setAddUserOpen(false);
      setAddUserRole("");
      addUserForm.resetFields();
      await fetchUsers();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.email?.[0] ?? d?.pharmacy_name?.[0] ?? d?.detail ?? "Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  };

  const applyUserFilters = async (search, role, status, org) => {
    setUserListLoading(true);
    try {
      const p = {};
      if (search)                p.search    = search;
      if (role)                  p.role      = role;
      if (status === "active")   p.is_active = "true";
      if (status === "suspended")p.is_active = "false";
      if (org && org !== "all")  p.org       = org;
      const res = await getUserList(p);
      setUserRows(toArray(res.data).map(r => ({ ...r, key: r.id })));
    } catch { /* silent */ }
    finally { setUserListLoading(false); }
  };

  const handleUserSearchChange = (e) => {
    const val = e.target.value;
    setUserSearch(val);
    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    const delay = val === "" ? 0 : 300;
    userSearchDebounceRef.current = setTimeout(() => {
      applyUserFilters(val, userRoleFilter, userStatusFilter, userOrgFilter);
    }, delay);
  };

  const handleUserRoleChange = (val) => {
    const v = val ?? "";
    setUserRoleFilter(v);
    applyUserFilters(userSearch, v, userStatusFilter, userOrgFilter);
  };

  const handleUserStatusChange = (val) => {
    setUserStatusFilter(val);
    applyUserFilters(userSearch, userRoleFilter, val, userOrgFilter);
  };

  const handleUserOrgChange = (val) => {
    setUserOrgFilter(val);
    applyUserFilters(userSearch, userRoleFilter, userStatusFilter, val);
  };

  const handleClearUserFilters = () => {
    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    setUserSearch("");
    setUserRoleFilter("");
    setUserStatusFilter("all");
    setUserOrgFilter("all");
    applyUserFilters("", "", "all", "all");
  };

  const fetchUsers = async () => {
    await applyUserFilters(userSearch, userRoleFilter, userStatusFilter, userOrgFilter);
  };

  const handleSuspendUser = async (user) => {
    try {
      await suspendUser(user.id);
      message.success("User suspended successfully.");
      await fetchUsers();
    } catch {
      message.error("Failed to suspend user.");
    }
  };

  const handleActivateUser = async (user) => {
    try {
      await activateUser(user.id);
      message.success("User activated successfully.");
      await fetchUsers();
    } catch {
      message.error("Failed to activate user.");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwTarget) return;
    if (resetPwMode === "specific") {
      if (resetPwSpecific.length < 8) {
        message.error("Password must be at least 8 characters."); return;
      }
      if (resetPwSpecific !== resetPwConfirm) {
        message.error("Passwords do not match."); return;
      }
    }
    setResetPwLoading(true);
    try {
      const body = resetPwMode === "specific" ? { password: resetPwSpecific } : {};
      const res = await resetUserPassword(resetPwTarget.id, body);
      setResetPwResult(res.data.new_password);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to reset password.");
      setResetPwTarget(null);
    } finally {
      setResetPwLoading(false);
    }
  };

  const handleReassignOrg = async () => {
    if (!reassignOrgTarget || !reassignOrgId) return;
    setSubmitting(true);
    try {
      await reassignUserOrg(reassignOrgTarget.id, reassignOrgId);
      message.success("User reassigned.");
      setReassignOrgOpen(false);
      setReassignOrgTarget(null);
      setReassignOrgId(null);
      await fetchUsers();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to reassign user.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewTicket = async (values) => {
    setSubmitting(true);
    try {
      await createTicket({
        subject:     values.subject,
        description: values.description,
        priority:    values.priority,
      });
      message.success("Support ticket created.");
      setNewTicketOpen(false);
      newTicketForm.resetFields();
      const res = await getAllTickets();
      setTicketRows(toArray(res.data).map(r=>({...r,key:r.id})));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.subject?.[0] ?? d?.detail ?? "Failed to create ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const refreshAuditLogs = async (params = auditFilters) => {
    setAuditRefreshing(true);
    try {
      const res = await getAuditLogs(params);
      setAuditRows(toArray(res.data).map(r=>({...r,key:r.id})));
      setAuditLastRefreshed(new Date());
    } catch {
      message.error("Failed to refresh audit logs.");
    } finally {
      setAuditRefreshing(false);
    }
  };

  const handleAuditSearch = (values) => {
    const params = {};
    if (values.actor_email)  params["actor__email"] = values.actor_email;
    if (values.action)       params.action           = values.action;
    if (values.target_model) params.target_model     = values.target_model;
    if (values.dateRange?.[0]) params.start_date = values.dateRange[0].startOf("day").toISOString();
    if (values.dateRange?.[1]) params.end_date   = values.dateRange[1].endOf("day").toISOString();
    setAuditFilters(params);
    refreshAuditLogs(params);
  };

  const handleAuditClear = () => {
    auditFilterForm.resetFields();
    setAuditFilters({});
    refreshAuditLogs({});
  };

  const handleRespond = async (values) => {
    if (!respondTarget) return;
    setSubmitting(true);
    try {
      await respondToTicket(respondTarget.id, {
        status:           values.status,
        resolution_notes: values.resolution_notes ?? "",
        ...(values.assigned_to ? { assigned_to: values.assigned_to } : {}),
      });
      notification.success({
        message: "Response submitted successfully",
        description: `Ticket status updated to ${values.status}.`,
      });
      setRespondOpen(false);
      respondForm.resetFields();
      setRespondTarget(null);
      const res = await getAllTickets();
      const rows = toArray(res.data).map(r=>({...r,key:r.id}));
      setTicketRows(rows);
      setStats(s => ({ ...s, openTickets: rows.filter(t=>t.status==="OPEN").length }));
    } catch (err) {
      const d = err?.response?.data;
      notification.error({ message: "Failed to submit response", description: d?.detail ?? "" });
    } finally {
      setSubmitting(false);
    }
  };

  const refreshTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await getAllTickets();
      const rows = toArray(res.data).map(r => ({ ...r, key: r.id }));
      setTicketRows(rows);
      setStats(s => ({ ...s, openTickets: rows.filter(t => t.status === "OPEN").length }));
    } catch {
      message.error("Failed to refresh tickets.");
    } finally {
      setTicketsLoading(false);
    }
  };

  const openAddConfig = () => {
    setConfigEditTarget(null);
    configForm.resetFields();
    setConfigModalOpen(true);
  };

  const openEditConfig = (record) => {
    setConfigEditTarget(record);
    configForm.setFieldsValue({ key: record.key, value: record.value, description: record.description ?? "" });
    setConfigModalOpen(true);
  };

  const handleSaveConfig = async (values) => {
    setSubmitting(true);
    try {
      if (configEditTarget) {
        const updated = await updateSystemConfig(configEditTarget.id, {
          value:       values.value,
          description: values.description ?? "",
        });
        setConfigRows(prev => prev.map(r =>
          r.id === configEditTarget.id ? { ...updated.data, _rowKey: r.id } : r
        ));
        message.success("Configuration updated.");
      } else {
        const created = await createSystemConfig({
          key:         values.key,
          value:       values.value,
          description: values.description ?? "",
        });
        setConfigRows(prev => [...prev, { ...created.data, _rowKey: created.data.id }]);
        message.success("Configuration entry added.");
      }
      setConfigModalOpen(false);
      configForm.resetFields();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.key?.[0] ?? d?.value?.[0] ?? d?.detail ?? "Failed to save configuration.");
    } finally {
      setSubmitting(false);
    }
  };

  // Lazy-load usage billing data on first visit to that section
  useEffect(() => {
    if (selectedMenu !== "usage-billing" || usageBillingLoaded) return;
    setUbLoading(true);
    Promise.all([
      getFeeConfigurations(),
      getMonthlyInvoices(),
      getUsageSummary(),
      getOrgBillingSummary(),
    ])
      .then(([feesRes, invoicesRes, summaryRes, orgBillSumRes]) => {
        setFeeRows(toArray(feesRes.data).map(r=>({...r,key:r.id})));
        setInvoiceRows(toArray(invoicesRes.data).map(r=>({...r,key:r.id})));
        setSummaryRows(Array.isArray(summaryRes.data) ? summaryRes.data.map((r,i)=>({...r,key:r.provider_id??i})) : []);
        setOrgBillingSummary(Array.isArray(orgBillSumRes.data) ? orgBillSumRes.data.map((r,i)=>({...r,key:r.organisation_id??i})) : []);
        setUsageBillingLoaded(true);
      })
      .catch(() => message.error("Failed to load usage billing data."))
      .finally(() => setUbLoading(false));
  }, [selectedMenu, usageBillingLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load pickup partners on first visit
  useEffect(() => {
    if (selectedMenu !== "pickup-partners" || ppLoaded) return;
    setPpLoading(true);
    listPickupPartners()
      .then(res => {
        setPpRows(toArray(res.data).map(r => ({ ...r, key: r.id })));
        setPpLoaded(true);
      })
      .catch(() => message.error("Failed to load pickup partners."))
      .finally(() => setPpLoading(false));
  }, [selectedMenu, ppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadPpRows = async () => {
    setPpLoading(true);
    try {
      const res = await listPickupPartners();
      setPpRows(toArray(res.data).map(r => ({ ...r, key: r.id })));
    } catch { /* silent */ } finally { setPpLoading(false); }
  };

  const openPpDetail = async (record) => {
    setPpDetailTarget(record);
    setPpDetailBilling(null);
    setPpDetailLoading(true);
    try {
      const res = await getPickupPartnerBilling(record.id);
      setPpDetailBilling(toArray(res.data));
    } catch { /* silent */ } finally { setPpDetailLoading(false); }
  };

  const handlePpCreate = async (values) => {
    setPpSubmitting(true);
    try {
      const payload = {
        name:          values.name,
        org_id:        values.org_id,
        partner_type:  values.partner_type ?? "PICKUP_DELIVERY",
        contact_email: values.contact_email,
        contact_phone: values.contact_phone ?? "",
        notes:         values.notes ?? "",
      };
      const res = await createPickupPartner(payload);
      setPpCreateResult(res.data);
      setPpCreateOpen(false);
      ppCreateForm.resetFields();
      reloadPpRows();
    } catch (err) {
      const d = err?.response?.data;
      message.error(
        d?.name?.[0] ?? d?.org_id?.[0] ?? d?.contact_email?.[0] ??
        d?.non_field_errors?.[0] ?? d?.detail ?? "Failed to create partner."
      );
    } finally { setPpSubmitting(false); }
  };

  const handlePpEdit = async (values) => {
    if (!ppEditTarget) return;
    setPpSubmitting(true);
    try {
      await updatePickupPartner(ppEditTarget.id, values);
      message.success("Partner organisation updated.");
      setPpEditOpen(false);
      ppEditForm.resetFields();
      setPpEditTarget(null);
      reloadPpRows();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.detail ?? "Failed to update partner.");
    } finally { setPpSubmitting(false); }
  };

  const handlePpToggleActive = async (record) => {
    try {
      await updatePickupPartner(record.id, { is_active: !record.is_active });
      message.success(`Partner ${record.is_active ? "deactivated" : "activated"}.`);
      reloadPpRows();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to update status.");
    }
  };

  const reloadUsageBilling = async () => {
    setUbLoading(true);
    try {
      const [feesRes, invoicesRes, summaryRes, orgBillSumRes] = await Promise.all([
        getFeeConfigurations(), getMonthlyInvoices(), getUsageSummary(), getOrgBillingSummary(),
      ]);
      setFeeRows(toArray(feesRes.data).map(r=>({...r,key:r.id})));
      setInvoiceRows(toArray(invoicesRes.data).map(r=>({...r,key:r.id})));
      setSummaryRows(Array.isArray(summaryRes.data) ? summaryRes.data.map((r,i)=>({...r,key:r.provider_id??i})) : []);
      setOrgBillingSummary(Array.isArray(orgBillSumRes.data) ? orgBillSumRes.data.map((r,i)=>({...r,key:r.organisation_id??i})) : []);
    } finally { setUbLoading(false); }
  };

  // Lazy-load partner orgs on first visit to any section that needs them
  useEffect(() => {
    const needsOrgs = ["partner-orgs", "users", "mchusa-org"].includes(selectedMenu);
    if (!needsOrgs || orgLoaded) return;
    setOrgLoading(true);
    listOrgs()
      .then(res => {
        setOrgRows(toArray(res.data).map(r => ({ ...r, key: r.id })));
        setOrgLoaded(true);
      })
      .catch(() => message.error("Failed to load organisations."))
      .finally(() => setOrgLoading(false));
  }, [selectedMenu, orgLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadOrgs = async () => {
    setOrgLoading(true);
    try {
      const res = await listOrgs();
      setOrgRows(toArray(res.data).map(r => ({ ...r, key: r.id })));
    } finally { setOrgLoading(false); }
  };

  const handleCreateOrg = async (values) => {
    setSubmitting(true);
    try {
      const res = await createOrg(values);
      setOrgCreateResult(res.data);
      message.success("Organisation created.");
      setOrgCreateOpen(false);
      orgCreateForm.resetFields();
      await reloadOrgs();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.name?.[0] ?? d?.ops_manager_email?.[0] ?? d?.detail ?? "Failed to create organisation.");
    } finally { setSubmitting(false); }
  };

  const handleEditOrg = async (values) => {
    if (!orgEditTarget) return;
    setSubmitting(true);
    try {
      await updateOrg(orgEditTarget.id, values);
      message.success("Organisation updated.");
      setOrgEditOpen(false);
      orgEditForm.resetFields();
      setOrgEditTarget(null);
      await reloadOrgs();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.name?.[0] ?? d?.detail ?? "Failed to update organisation.");
    } finally { setSubmitting(false); }
  };

  const handleDeactivateOrg = async (org) => {
    try {
      await deactivateOrg(org.id);
      message.success(`${org.name} deactivated.`);
      await reloadOrgs();
      if (orgDetailTarget?.id === org.id) setOrgDetailTarget(null);
    } catch {
      message.error("Failed to deactivate organisation.");
    }
  };

  const handleToggleModule = async (module, enabled, org = orgDetailTarget) => {
    if (!org) return;
    const current = org.subscribed_modules ?? [];
    const updated = enabled
      ? [...current.filter(m => m !== module), module]
      : current.filter(m => m !== module);
    setOrgModulesSaving(true);
    try {
      await updateOrg(org.id, { subscribed_modules: updated });
      const patched = { ...org, subscribed_modules: updated };
      if (orgDetailTarget?.id === org.id) setOrgDetailTarget(patched);
      setOrgRows(prev => prev.map(r => r.id === org.id ? { ...r, subscribed_modules: updated } : r));
      message.success(`${module} module ${enabled ? "enabled" : "disabled"} for ${org.name}.`);
    } catch {
      message.error("Failed to update modules.");
    } finally {
      setOrgModulesSaving(false);
    }
  };

  const openOrgDetail = async (org) => {
    setOrgDetailTarget(org);
    setOrgDetailStats(null);
    setOrgBillingData(null);
    setOrgBillingMonth(null);
    setOrgInvoices([]);
    setOrgDetailLoading(true);
    setOrgBillingLoading(true);
    setOrgInvoicesLoading(true);
    try {
      const [statsRes, billingRes, invoicesRes] = await Promise.all([
        getOrgStats(org.id),
        getOrgBilling(org.id, null),
        listOrgInvoices(org.id),
      ]);
      setOrgDetailStats(statsRes.data);
      setOrgBillingData(billingRes.data);
      setOrgInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : (invoicesRes.data?.results ?? []));
    } catch {
      message.error("Failed to load organisation details.");
    } finally {
      setOrgDetailLoading(false);
      setOrgBillingLoading(false);
      setOrgInvoicesLoading(false);
    }
  };

  const loadOrgBilling = async (month) => {
    if (!orgDetailTarget) return;
    setOrgBillingLoading(true);
    try {
      const res = await getOrgBilling(orgDetailTarget.id, month ?? undefined);
      setOrgBillingData(res.data);
    } catch {
      message.error("Failed to load billing data.");
    } finally { setOrgBillingLoading(false); }
  };

  const exportBillingCSV = () => {
    if (!orgBillingData) return;
    const headers = ["Date", "Type", "User Email", "Amount", "Description"];
    const rows = orgBillingData.events.map(e => [
      e.event_date ? new Date(e.event_date).toLocaleDateString() : "",
      e.event_type,
      e.user_email ?? "",
      parseFloat(e.amount).toFixed(2),
      e.description,
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${orgBillingData.organisation_name}-${orgBillingData.month}-billing.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateOrgInvoice = async (month, year) => {
    if (!orgDetailTarget) return;
    setInvoiceGenLoading(true);
    try {
      const res = await generateOrgInvoice(orgDetailTarget.id, { month, year });
      const inv = res.data;
      notification.success({
        message: `Invoice ${inv.invoice_number} sent to ${orgDetailTarget.name}.`,
        description: inv.due_date
          ? `Due date: ${new Date(inv.due_date).toLocaleDateString("en-GB")}`
          : "",
      });
      const invRes = await listOrgInvoices(orgDetailTarget.id);
      setOrgInvoices(Array.isArray(invRes.data) ? invRes.data : (invRes.data?.results ?? []));
    } catch (err) {
      const d = err?.response?.data;
      notification.error({ message: "Failed to generate invoice", description: d?.detail ?? "" });
    } finally {
      setInvoiceGenLoading(false);
    }
  };

  const handleMarkInvoicePaid = async (invoice) => {
    try {
      const res = await markInvoicePaid(invoice.id);
      setOrgInvoices(prev => prev.map(inv => inv.id === invoice.id ? res.data : inv));
      setInvoiceDetailTarget(res.data);
      message.success("Invoice marked as paid.");
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to mark invoice as paid.");
    }
  };

  const handleEditFee = async (values) => {
    setSubmitting(true);
    try {
      if (editFeeTarget?.id) {
        await updateFeeConfiguration(editFeeTarget.id, {
          amount: values.amount,
          description: values.description ?? "",
          is_active: values.is_active ?? true,
        });
      } else {
        await createFeeConfiguration({
          fee_type: values.fee_type,
          amount: values.amount,
          description: values.description ?? "",
          is_active: true,
        });
      }
      message.success("Fee configuration saved.");
      setEditFeeOpen(false); setAddFeeOpen(false);
      editFeeForm.resetFields(); addFeeForm.resetFields();
      setEditFeeTarget(null);
      await reloadUsageBilling();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.amount?.[0] ?? d?.fee_type?.[0] ?? d?.detail ?? "Failed to save fee.");
    } finally { setSubmitting(false); }
  };

  const handleGenerateInvoices = async () => {
    if (!generateMonth) { message.warning("Please select a billing month."); return; }
    const monthStr = generateMonth.format("YYYY-MM");
    setSubmitting(true);
    try {
      const res = await generateInvoices(monthStr);
      setGenerateResult(res.data);
      message.success(res.data.message ?? "Invoices generated.");
      await reloadUsageBilling();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.billing_month?.[0] ?? d?.detail ?? "Failed to generate invoices.");
    } finally { setSubmitting(false); }
  };

  const handleMarkInvoiceStatus = async (invoice, newStatus) => {
    try {
      await updateInvoiceStatus(invoice.id, { status: newStatus });
      message.success(`Invoice marked as ${newStatus}.`);
      setInvoiceDetailTarget(null);
      await reloadUsageBilling();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to update invoice.");
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [{ key:"logout", icon:<LogoutOutlined />, label:"Logout", danger:true, onClick:handleLogout }];

  // ── column defs ─────────────────────────────────────────────────────────────
  const USER_COLS = [
    { title:"Email",     dataIndex:"email",      key:"email",      ellipsis:true },
    { title:"Role",      dataIndex:"role",       key:"role",       width:150, render:r=><Tag color="blue">{r?.replace(/_/g," ")}</Tag> },
    { title:"Organisation", dataIndex:"organisation_name", key:"org", width:160,
      render:n=>n?<Tag color="geekblue" style={{fontSize:11}}>{n}</Tag>:<Text type="secondary" style={{fontSize:11}}>—</Text> },
    { title:"Status",    dataIndex:"is_active",  key:"is_active",  width:110,
      render: v => (
        <Space size={4}>
          <span style={{
            display:"inline-block", width:8, height:8, borderRadius:"50%",
            background: v ? "#52c41a" : "#ff4d4f",
          }} />
          <Text style={{ color: v ? "#52c41a" : "#ff4d4f", fontSize:13 }}>
            {v ? "Active" : "Suspended"}
          </Text>
        </Space>
      ),
    },
    { title:"MFA",       dataIndex:"mfa_enabled",key:"mfa_enabled",width:70,  render:v=>v?<Tag color="purple">On</Tag>:<Tag>Off</Tag> },
    { title:"Joined",    dataIndex:"created_at", key:"created_at", width:120, render:fmtDate },
    { title:"Actions",   key:"actions",          width:240,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.is_active ? (
            <Popconfirm
              title={`Suspend ${r.email}?`}
              description="They will lose access immediately."
              onConfirm={() => handleSuspendUser(r)}
              okText="Suspend" okButtonProps={{ danger: true }}
              cancelText="Cancel">
              <Button size="small" danger>Suspend</Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`Reactivate ${r.email}?`}
              onConfirm={() => handleActivateUser(r)}
              okText="Activate" cancelText="Cancel">
              <Button size="small" style={{ color: "#52c41a", borderColor: "#52c41a" }}>Activate</Button>
            </Popconfirm>
          )}
          <Button size="small" onClick={()=>{setResetPwTarget(r);setResetPwResult(null);}}>
            Reset PW
          </Button>
          <Button size="small" onClick={()=>{setReassignOrgTarget(r);setReassignOrgId(r.organisation_id??null);setReassignOrgOpen(true);}}>
            Reassign
          </Button>
        </Space>
      ),
    },
  ];

  const ACTION_COLOR = {
    CREATE:"green", LIST:"blue", RETRIEVE:"cyan", UPDATE:"orange",
    PARTIAL_UPDATE:"orange", DELETE:"red", ASSIGN:"purple",
    DISPENSE:"geekblue", CANCEL:"volcano",
  };

  const AUDIT_COLS = [
    { title:"Timestamp",  dataIndex:"timestamp",    key:"timestamp",   width:170,
      render: iso => iso ? new Date(iso).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : "—" },
    { title:"User",       dataIndex:"actor_email",  key:"actor_email", ellipsis:true,
      render: e => e ?? <Text type="secondary">deleted</Text> },
    { title:"Role",       dataIndex:"actor_role",   key:"actor_role",  width:130,
      render: r => r ? <Tag color="blue" style={{fontSize:11}}>{r.replace(/_/g," ")}</Tag> : "—" },
    { title:"Action",     dataIndex:"action",       key:"action",      width:150,
      render: a => {
        const base = a?.split("_")?.[0] ?? a;
        return <Tag color={ACTION_COLOR[base] ?? ACTION_COLOR[a] ?? "default"} style={{fontSize:11}}>{a}</Tag>;
      }},
    { title:"Module",     dataIndex:"target_model", key:"target_model",width:160,
      render: m => m ? <Tag color="geekblue" style={{fontSize:11}}>{m}</Tag> : "—" },
    { title:"Target ID",  dataIndex:"target_id",    key:"target_id",   width:100,
      render: id => id ? <Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> : "—" },
    { title:"IP Address", dataIndex:"ip_address",   key:"ip_address",  width:125,
      render: ip => ip ?? "—" },
  ];

  const TICKET_COLS = [
    { title:"ID",            dataIndex:"id",                key:"id",                width:90,
      render: id => <Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
    { title:"Subject",       dataIndex:"subject",           key:"subject",           ellipsis:true },
    { title:"Organisation",  dataIndex:"organisation_name", key:"org",               width:160,
      render: n => n ? <Tag color="blue" style={{fontSize:11}}>{n}</Tag> : <Text type="secondary" style={{fontSize:11}}>MCHUSA Internal</Text> },
    { title:"Submitted By",  dataIndex:"submitted_by_email",key:"submitted_by_email",ellipsis:true, render:e=>e??"—" },
    { title:"Category",      dataIndex:"category",          key:"category",          width:130,
      render:c=>c?<Tag style={{fontSize:11}}>{c?.replace(/_/g," ")}</Tag>:"—" },
    { title:"Priority",      dataIndex:"priority",          key:"priority",          width:100, render:p=><Tag color={PRIORITY_COLOR[p]??"default"}>{p}</Tag> },
    { title:"Status",        dataIndex:"status",            key:"status",            width:110, render:s=><Tag color={TICKET_STATUS_COLOR[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
    { title:"Created",       dataIndex:"created_at",        key:"created_at",        width:110, render:fmtDate },
    { title:"Actions",       key:"actions",                 width:150,
      render:(_,r) => (
        <Space size={4}>
          <Button size="small" onClick={()=>setTicketDetail(r)}>View</Button>
          {r.status !== "CLOSED" && (
            <Button size="small" type="primary" onClick={()=>{
              setRespondTarget(r);
              respondForm.setFieldsValue({
                status:           r.status === "ESCALATED" ? "RESOLVED" : r.status,
                resolution_notes: r.resolution_notes ?? "",
              });
              setRespondOpen(true);
            }}>
              Respond
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const CONFIG_COLS = [
    { title:"Key",        dataIndex:"key",         key:"key",         render:k=><Text code>{k}</Text> },
    { title:"Value",      dataIndex:"value",        key:"value",       ellipsis:true },
    { title:"Description",dataIndex:"description",  key:"description", ellipsis:true,
      render:v=>v||<Text type="secondary">—</Text> },
    { title:"Updated",    dataIndex:"updated_at",   key:"updated_at",  width:120, render:fmtDate },
    { title:"Actions",    key:"actions",             width:80,
      render:(_,r)=><Button size="small" icon={<EditOutlined />} onClick={()=>openEditConfig(r)}>Edit</Button> },
  ];

  const BILLING_COLS = [ // eslint-disable-line no-unused-vars
    { title:"Patient",      dataIndex:"patient_email",         key:"patient_email",         render:e=>e??"—" },
    { title:"Provider",     dataIndex:"service_provider_email",key:"service_provider_email",render:e=>e??"—" },
    { title:"Service Date", dataIndex:"service_date",          key:"service_date",          width:120, render:fmtDate },
    { title:"Amount Billed",dataIndex:"amount_billed",         key:"amount_billed",         width:130, render:a=>a!=null?`$${parseFloat(a).toFixed(2)}`:"—" },
    { title:"Payor",        dataIndex:"payor_type",            key:"payor_type",            width:100, render:t=>t?<Tag>{t}</Tag>:"—" },
    { title:"Status",       dataIndex:"submission_status",     key:"submission_status",     width:110, render:s=><Tag color={BILLING_STATUS_COLOR[s]??"default"}>{s??"—"}</Tag> },
  ];

  const ACTIVITY_COLS = [
    { title:"Timestamp", dataIndex:"timestamp",   key:"timestamp",   width:170, render:fmt },
    { title:"User",      dataIndex:"actor_email", key:"actor_email", render:e=>e??<Text type="secondary">deleted user</Text> },
    { title:"Action",    dataIndex:"action",      key:"action" },
    { title:"Module",    dataIndex:"target_model",key:"target_model",render:m=>m?<Tag color="blue">{m}</Tag>:<Tag>—</Tag> },
    { title:"Status",    key:"status",            render:()=><Tag color="green">Success</Tag> },
  ];

  // ── views ────────────────────────────────────────────────────────────────────
  const statCards = [
    { title:"Total Users",        value:stats.totalUsers,        color:"#1677ff" },
    { title:"Active Sessions",    value:0,                       color:"#52c41a" },
    { title:"Open Tickets",       value:stats.openTickets,       color:"#fa8c16" },
    { title:"Audit Events Today", value:stats.auditEventsToday,  color:"#722ed1" },
  ];

  const fetchSignupRequests = async () => {
    setSignupLoading(true);
    try {
      const res = await getSignupRequests();
      setSignupRows((res.data?.results ?? res.data ?? []).map((r, i) => ({ ...r, key: r.id ?? i })));
    } catch { /* silent */ } finally {
      setSignupLoading(false);
    }
  };

  const handleApproveSignup = async () => {
    try {
      await approveSignupRequest(approveModal.id, approveOrg ? { organisation_id: approveOrg } : {});
      message.success(`Account created for ${approveModal.email}`);
      setApproveModal(null);
      setApproveOrg(null);
      setSignupRows(prev => prev.map(r => r.id === approveModal.id ? { ...r, status: "APPROVED" } : r));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Approval failed.");
    }
  };

  const handleRejectSignup = async () => {
    try {
      await rejectSignupRequest(signupRejectModal.id, rejectReason);
      message.success("Request rejected.");
      setSignupRejectModal(null);
      setRejectReason("");
      setSignupRows(prev => prev.map(r => r.id === signupRejectModal.id ? { ...r, status: "REJECTED" } : r));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Rejection failed.");
    }
  };

  const MODULE_DEFS = [
    {
      key: "DOCTOR",
      label: "Doctor Module",
      icon: <UserOutlined style={{fontSize:24,color:"#722ed1"}} />,
      description: "Doctor portal, patient enrollment, diagnoses, ICD-10, peer consultations, appointments",
      billing: "Billed per: Patient Registration, Prescription Issued",
    },
    {
      key: "PATIENT",
      label: "Patient Module",
      icon: <HeartOutlined style={{fontSize:24,color:"#eb2f96"}} />,
      description: "Patient portal, medical history, prescription tracking, proxy users",
      billing: "Billed per: Patient Registration",
    },
    {
      key: "PHARMACY",
      label: "Pharmacy Module",
      icon: <AccountBookOutlined style={{fontSize:24,color:"#52c41a"}} />,
      description: "Prescription queue, medication dispensing, inventory management, billing submission",
      billing: "Billed per: Prescription Dispensed",
    },
    {
      key: "OPHTHALMOLOGY",
      label: "Ophthalmology & Optician",
      icon: <EyeOutlined style={{fontSize:24,color:"#1677ff"}} />,
      description: "Eye examinations, lens prescriptions, glasses fitting and ordering",
      billing: "Billed per: Eye Examination, Lens Prescription Issued",
    },
    {
      key: "EMS",
      label: "EMS Module",
      icon: <AlertOutlined style={{fontSize:24,color:"#f5222d"}} />,
      description: "Emergency dispatch, ambulance coordination, patient transport to hospitals",
      billing: "No additional per-event billing",
    },
    {
      key: "BILLING",
      label: "Billing & Claims",
      icon: <FileTextOutlined style={{fontSize:24,color:"#fa8c16"}} />,
      description: "Insurance carrier submissions, State Government billing, payor-compliant documentation",
      billing: "No additional per-event billing",
    },
  ];

  function renderContent() {
    switch (selectedMenu) {
      case "signup-requests": {
        const pending  = signupRows.filter(r => r.status === "PENDING");
        const reviewed = signupRows.filter(r => r.status !== "PENDING");
        const STATUS_COLOR = { PENDING: "gold", APPROVED: "green", REJECTED: "red" };
        const ROLE_COLOR   = { DOCTOR: "blue", PATIENT: "green", PHARMACIST: "purple" };
        const cols = [
          { title: "Name",         key: "name",    render: (_, r) => `${r.first_name} ${r.last_name}` },
          { title: "Email",        dataIndex: "email",       key: "email" },
          { title: "Role",         dataIndex: "role",        key: "role",   width: 110, render: v => <Tag color={ROLE_COLOR[v] ?? "default"}>{v}</Tag> },
          { title: "Organisation", key: "org",     width: 160, render: (_, r) => r.organisation_name ?? <Text type="secondary">Not specified</Text> },
          { title: "Submitted",    dataIndex: "created_at",  key: "created_at", width: 120, render: v => v ? new Date(v).toLocaleDateString() : "—" },
          { title: "Status",       dataIndex: "status",      key: "status", width: 100, render: s => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
          { title: "Actions",      key: "actions", width: 160,
            render: (_, r) => r.status !== "PENDING" ? null : (
              <Space>
                <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => { setApproveModal(r); setApproveOrg(r.organisation ?? null); }}>
                  Approve
                </Button>
                <Button size="small" danger icon={<CloseCircleOutlined />}
                  onClick={() => { setSignupRejectModal(r); setRejectReason(""); }}>
                  Reject
                </Button>
              </Space>
            ),
          },
        ];
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <Title level={4} style={{ margin: 0, color: "#262626" }}>Signup Requests</Title>
              <Button icon={<ReloadOutlined />} onClick={fetchSignupRequests} loading={signupLoading}>Refresh</Button>
            </div>
            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title={<><Badge count={pending.length} offset={[8, 0]}><Text strong>Pending Review</Text></Badge></>}>
              <Table columns={cols} dataSource={pending} loading={signupLoading} size="small"
                pagination={false} locale={{ emptyText: "No pending signup requests" }} />
            </Card>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Reviewed</Text>}>
              <Table columns={cols} dataSource={reviewed} loading={signupLoading} size="small"
                pagination={{ pageSize: 10 }} locale={{ emptyText: "No reviewed requests yet" }} />
            </Card>

            {/* Approve modal */}
            <Modal title="Approve Signup Request" open={!!approveModal}
              onOk={handleApproveSignup} onCancel={() => setApproveModal(null)}
              okText="Create Account" okButtonProps={{ type: "primary" }}>
              {approveModal && (
                <>
                  <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Name">{approveModal.first_name} {approveModal.last_name}</Descriptions.Item>
                    <Descriptions.Item label="Email">{approveModal.email}</Descriptions.Item>
                    <Descriptions.Item label="Role"><Tag color={ROLE_COLOR[approveModal.role]}>{approveModal.role}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Requested Org">{approveModal.organisation_name ?? "Not specified"}</Descriptions.Item>
                    {approveModal.notes && <Descriptions.Item label="Notes">{approveModal.notes}</Descriptions.Item>}
                  </Descriptions>
                  <Form layout="vertical">
                    <Form.Item label="Assign Organisation (override optional)">
                      <Select allowClear showSearch placeholder="Keep requested org or pick one"
                        value={approveOrg}
                        onChange={setApproveOrg}
                        filterOption={(i, o) => o?.label?.toLowerCase().includes(i.toLowerCase())}
                        options={(orgRows.length ? orgRows : []).map(o => ({ value: o.id, label: o.name }))}
                      />
                    </Form.Item>
                  </Form>
                </>
              )}
            </Modal>

            {/* Reject modal */}
            <Modal title="Reject Signup Request" open={!!signupRejectModal}
              onOk={handleRejectSignup} onCancel={() => setSignupRejectModal(null)}
              okText="Reject Request" okButtonProps={{ danger: true }}>
              {signupRejectModal && (
                <>
                  <Text>Rejecting request from <strong>{signupRejectModal.email}</strong> ({signupRejectModal.role})</Text>
                  <Form layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item label="Reason for rejection (optional)">
                      <Input.TextArea rows={3} value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="e.g. Invalid license number, duplicate account…" />
                    </Form.Item>
                  </Form>
                </>
              )}
            </Modal>
          </>
        );
      }

      case "dashboard":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>System Overview</Title>
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              {statCards.map(({title,value,color})=>(
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{borderRadius:8}}>
                    <Statistic
                      title={<Text style={{color:"#8c8c8c",fontSize:13}}>{title}</Text>}
                      value={value}
                      styles={{ content: {color,fontSize:28,fontWeight:600} }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
            <Row gutter={[16,16]}>
              <Col xs={24} lg={16}>
                <Card title={<Text strong>Recent Activity</Text>} variant="borderless" style={{borderRadius:8}}>
                  <Table columns={ACTIVITY_COLS} dataSource={activityRows}
                    pagination={{pageSize:10,hideOnSinglePage:true}}
                    locale={{emptyText:"No activity recorded yet"}} size="small" />
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title={<Text strong>System Status</Text>} variant="borderless" style={{borderRadius:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0"}}>
                    <Badge status="success" />
                    <Text style={{color:"#52c41a",fontWeight:500}}>All Systems Operational</Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "users": {
        const hasActiveFilters = !!(userSearch || userRoleFilter || userStatusFilter !== "all" || userOrgFilter !== "all");
        const ROLE_OPTIONS = [
          {label:"Doctor",             value:"DOCTOR"},
          {label:"Patient",            value:"PATIENT"},
          {label:"Pharmacist",         value:"PHARMACIST"},
          {label:"Ophthalmologist",    value:"OPHTHALMOLOGIST"},
          {label:"Optician",           value:"OPTICIAN"},
          {label:"EMS Dispatcher",     value:"EMS_DISPATCHER"},
          {label:"EMS Driver",         value:"EMS_DRIVER"},
          {label:"Billing Staff",      value:"BILLING_STAFF"},
          {label:"System Admin",       value:"SYSTEM_ADMIN"},
          {label:"Shareholder",        value:"SHAREHOLDER"},
          {label:"Operations Manager", value:"OPERATIONS_MANAGER"},
          {label:"Org Administrator",  value:"ORGANISATION_ADMINISTRATOR"},
          {label:"Partner Org",        value:"PARTNER_ORG"},
        ];
        return (
          <>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>User Management</Title>

            {/* Search and filter bar */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:16}}>
              <Input
                prefix={<SearchOutlined style={{color:"#bfbfbf"}} />}
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={handleUserSearchChange}
                allowClear
                style={{marginBottom:12}}
              />
              <Space wrap size={8}>
                <Select
                  value={userRoleFilter || undefined}
                  onChange={handleUserRoleChange}
                  placeholder="All Types"
                  allowClear
                  style={{width:190}}
                  size="small"
                >
                  {ROLE_OPTIONS.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Select value={userStatusFilter} onChange={handleUserStatusChange}
                  style={{width:140}} size="small">
                  <Option value="all">All Status</Option>
                  <Option value="active">Active</Option>
                  <Option value="suspended">Inactive</Option>
                </Select>
                <Select value={userOrgFilter} onChange={handleUserOrgChange}
                  style={{width:210}} size="small">
                  <Option value="all">All Organisations</Option>
                  {orgRows.map(o => (
                    <Option key={o.id} value={o.id}>{o.name}{o.is_owner ? " (Owner)" : ""}</Option>
                  ))}
                </Select>
                {hasActiveFilters && (
                  <Button size="small" onClick={handleClearUserFilters}>Clear Filters</Button>
                )}
              </Space>
              <div style={{marginTop:8}}>
                <Text type="secondary" style={{fontSize:12}}>
                  Showing {userRows.length} user{userRows.length !== 1 ? "s" : ""}
                </Text>
              </div>
            </Card>

            {/* User table */}
            <Card variant="borderless" style={{borderRadius:8}}
              title={<Text strong>All Users</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setAddUserOpen(true)}>Add User</Button>}>
              <Table columns={USER_COLS} dataSource={userRows}
                loading={userListLoading}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText: hasActiveFilters ? (
                  <Space direction="vertical" size={8} style={{padding:"24px 0"}}>
                    <Text type="secondary">No users found matching your filters.</Text>
                    <Button size="small" onClick={handleClearUserFilters}>Clear Filters</Button>
                  </Space>
                ) : "No users found"}}
                size="small"
                scroll={{x:1100}}
                rowClassName={r => r.is_active ? "" : "ant-table-row-suspended"}
                onRow={r => ({ style: r.is_active ? {} : { opacity: 0.55 } })}
              />
            </Card>
          </>
        );
      }

      case "audit-logs":
        return (
          <>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Audit Logs</Title>

            {/* ── Filter Bar ── */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:16}}>
              <Form form={auditFilterForm} layout="inline" onFinish={handleAuditSearch}
                style={{gap:8,flexWrap:"wrap"}}>
                <Form.Item name="actor_email" style={{marginBottom:8}}>
                  <Input placeholder="Search by user email" style={{width:200}} allowClear />
                </Form.Item>
                <Form.Item name="action" style={{marginBottom:8}}>
                  <Select placeholder="Action" allowClear style={{width:160}}>
                    {["LIST","CREATE","RETRIEVE","UPDATE","PARTIAL_UPDATE","DELETE",
                      "ASSIGN","DISPENSE","CANCEL"].map(a=>(
                      <Option key={a} value={a}>{a}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="target_model" style={{marginBottom:8}}>
                  <Select placeholder="Module" allowClear style={{width:180}}>
                    {["Prescription","EMSRequest","BillingRecord","PatientProfile",
                      "DoctorProfile","AuditLog","SupportTicket","MonthlyInvoice",
                      "FeeConfiguration","Diagnosis","MedicalHistoryShare"].map(m=>(
                      <Option key={m} value={m}>{m}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="dateRange" style={{marginBottom:8}}>
                  <DatePicker.RangePicker style={{width:240}} />
                </Form.Item>
                <Form.Item style={{marginBottom:8}}>
                  <Space>
                    <Button type="primary" htmlType="submit">Search</Button>
                    <Button onClick={handleAuditClear}>Clear</Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>

            {/* ── Table ── */}
            <Card variant="borderless" style={{borderRadius:8}}
              title={<Text strong>Log Entries</Text>}
              extra={
                <Space>
                  {auditLastRefreshed && (
                    <Text type="secondary" style={{fontSize:12}}>
                      Last refreshed: {auditLastRefreshed.toLocaleTimeString()}
                    </Text>
                  )}
                  <Button size="small" icon={<ReloadOutlined />}
                    loading={auditRefreshing} onClick={()=>refreshAuditLogs()}>
                    Refresh
                  </Button>
                </Space>
              }>
              <Table columns={AUDIT_COLS} dataSource={auditRows}
                pagination={{pageSize:20,hideOnSinglePage:true}}
                locale={{emptyText:"No audit log entries"}} size="small"
                scroll={{x:1000}} />
            </Card>
          </>
        );

      case "support-tickets": {
        const openCount    = ticketRows.filter(t=>t.status==="OPEN").length;
        const inProgCount  = ticketRows.filter(t=>t.status==="IN_PROGRESS").length;
        const resolvedToday= ticketRows.filter(t=>t.status==="RESOLVED"&&isToday(t.updated_at)).length;
        const escalatedCount = ticketRows.filter(t => t.status === "ESCALATED" || t.ticket_level === 3).length;
        const displayTickets = ticketSourceFilter === "PARTNER"
          ? ticketRows.filter(t => t.organisation_name)
          : ticketSourceFilter === "INTERNAL"
          ? ticketRows.filter(t => !t.organisation_name)
          : ticketSourceFilter === "ESCALATED"
          ? ticketRows.filter(t => t.status === "ESCALATED" || t.ticket_level === 3)
          : ticketRows;
        return (
          <>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Support Tickets</Title>
            <Row gutter={[16,16]} style={{marginBottom:16}}>
              {[
                {title:"Open",            value:openCount,       color:"#fa8c16"},
                {title:"In Progress",     value:inProgCount,     color:"#1677ff"},
                {title:"Resolved Today",  value:resolvedToday,   color:"#52c41a"},
                {title:"Escalated",       value:escalatedCount,  color:"#cf1322"},
              ].map(({title,value,color})=>(
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{borderRadius:8}}>
                    <Statistic title={<Text style={{color:"#8c8c8c",fontSize:13}}>{title}</Text>}
                      value={value} styles={{ content: {color,fontSize:26,fontWeight:600} }} />
                  </Card>
                </Col>
              ))}
            </Row>
            <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>All Tickets</Text>}
              extra={
                <Space size={8}>
                  <Button size="small" icon={<ReloadOutlined />} loading={ticketsLoading} onClick={refreshTickets}>Refresh</Button>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setNewTicketOpen(true)}>New Ticket</Button>
                </Space>
              }>
              <Space style={{marginBottom:12}}>
                <Text type="secondary" style={{fontSize:13}}>Source:</Text>
                {[
                  {key:"ALL",      label:"All Sources"},
                  {key:"PARTNER",  label:"Partner Orgs"},
                  {key:"INTERNAL", label:"Internal"},
                  {key:"ESCALATED",label:"Escalated"},
                ].map(({key:s,label})=>(
                  <Button key={s} size="small"
                    type={ticketSourceFilter===s?"primary":"default"}
                    danger={s==="ESCALATED" && ticketSourceFilter!==s && escalatedCount>0}
                    onClick={()=>setTicketSourceFilter(s)}>
                    {label}{s==="ESCALATED" && escalatedCount>0 ? ` (${escalatedCount})` : ""}
                  </Button>
                ))}
              </Space>
              <Table columns={TICKET_COLS} dataSource={displayTickets}
                loading={ticketsLoading}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No support tickets"}} size="small" scroll={{x:1100}} />
            </Card>
          </>
        );
      }

      case "config":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>System Configuration</Title>
            <Card variant="borderless" style={{borderRadius:8}}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddConfig}>Add Config</Button>}>
              <Table
                columns={CONFIG_COLS}
                dataSource={configRows}
                rowKey="_rowKey"
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{
                  emptyText:(
                    <div style={{textAlign:"center",padding:"32px 0"}}>
                      <SettingOutlined style={{fontSize:36,color:"#d9d9d9",marginBottom:8,display:"block"}} />
                      <Text type="secondary" style={{display:"block",marginBottom:12}}>No configuration entries yet.</Text>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openAddConfig}>Add Config</Button>
                    </div>
                  ),
                }}
                size="small"
              />
            </Card>
          </>
        );

      case "usage-billing":
        return (
          <Spin spinning={ubLoading} description="Loading…">
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Usage &amp; Billing</Title>

            {/* ── Fee Configuration ── */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:24}}
              title={<Typography.Text strong>Fee Configuration</Typography.Text>}
              extra={<Button size="small" icon={<PlusOutlined />} onClick={()=>setAddFeeOpen(true)}>Add Fee</Button>}>
              <Table
                size="small"
                dataSource={feeRows}
                pagination={false}
                locale={{emptyText:"No fee configurations"}}
                columns={[
                  { title:"Fee Type", dataIndex:"fee_type", key:"fee_type",
                    render:v=><Tag color="blue">{FEE_TYPE_LABEL[v]??v}</Tag> },
                  { title:"Description", dataIndex:"description", key:"description", ellipsis:true,
                    render:v=>v||<Typography.Text type="secondary">—</Typography.Text> },
                  { title:"Amount ($)", dataIndex:"amount", key:"amount", width:120,
                    render:v=><Typography.Text strong>${parseFloat(v).toFixed(2)}</Typography.Text> },
                  { title:"Status", dataIndex:"is_active", key:"is_active", width:90,
                    render:v=>v?<Tag color="green">Active</Tag>:<Tag>Inactive</Tag> },
                  { title:"Updated", dataIndex:"updated_at", key:"updated_at", width:120, render:fmtDate },
                  { title:"Actions", key:"actions", width:90,
                    render:(_,r)=>(
                      <Button size="small" onClick={()=>{
                        setEditFeeTarget(r);
                        editFeeForm.setFieldsValue({amount:parseFloat(r.amount),description:r.description,is_active:r.is_active});
                        setEditFeeOpen(true);
                      }}>Edit</Button>
                    ),
                  },
                ]}
              />
            </Card>

            {/* ── Current Month Usage Summary ── */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:24}}
              title={<Typography.Text strong>Current Month Usage Summary</Typography.Text>}>
              <Table
                size="small"
                dataSource={summaryRows}
                pagination={false}
                locale={{emptyText:"No usage data for current month"}}
                columns={[
                  { title:"Provider", dataIndex:"provider_email", key:"provider_email" },
                  { title:"Role", dataIndex:"provider_role", key:"provider_role",
                    render:v=><Tag color="blue">{v}</Tag> },
                  { title:"Registrations", dataIndex:"registrations", key:"registrations", width:120 },
                  { title:"Rx Issued", dataIndex:"prescriptions_issued", key:"prescriptions_issued", width:95 },
                  { title:"Rx Dispensed", dataIndex:"prescriptions_dispensed", key:"prescriptions_dispensed", width:110 },
                  { title:"Eye Exams", dataIndex:"eye_examinations", key:"eye_examinations", width:100 },
                  { title:"Lens Rx", dataIndex:"lens_prescriptions_issued", key:"lens_prescriptions_issued", width:90 },
                  { title:"Estimated Fee", dataIndex:"estimated_fee", key:"estimated_fee", width:130,
                    render:v=><Typography.Text strong>${parseFloat(v).toFixed(2)}</Typography.Text> },
                ]}
              />
            </Card>

            {/* ── Generate Monthly Invoices ── */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:24}}
              title={<Typography.Text strong>Generate Monthly Invoices</Typography.Text>}>
              <Row gutter={12} align="middle" style={{marginBottom:generateResult?16:0}}>
                <Col>
                  <DatePicker picker="month" format="YYYY-MM"
                    onChange={setGenerateMonth}
                    placeholder="Select billing month" />
                </Col>
                <Col>
                  <Button type="primary" loading={submitting} onClick={handleGenerateInvoices}>
                    Generate Invoices
                  </Button>
                </Col>
              </Row>
              {generateResult && (
                <Alert type="success" showIcon style={{marginTop:12}}
                  message={generateResult.message}
                  description={`${generateResult.invoices?.length??0} invoice(s) created/updated.`}
                />
              )}
            </Card>

            {/* ── Partner Organisation Billing ── */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:24}}
              title={<Typography.Text strong>Partner Organisation Billing — Current Month</Typography.Text>}>
              <Table
                size="small"
                dataSource={orgBillingSummary}
                pagination={false}
                locale={{emptyText:"No partner billing data for current month"}}
                scroll={{x:900}}
                columns={[
                  { title:"Organisation", dataIndex:"organisation_name", key:"org_name",
                    render:(n,r)=>r.is_active?n:<span style={{opacity:0.5}}>{n} (inactive)</span> },
                  { title:"Rates", key:"rates", width:95,
                    render:(_,r)=>r.has_custom_rates
                      ?<Tag color="blue"  style={{fontSize:11}}>Custom</Tag>
                      :<Tag color="default" style={{fontSize:11}}>Standard</Tag> },
                  { title:"Registrations",   dataIndex:"registrations",    key:"regs",      width:115 },
                  { title:"Rx Issued",       dataIndex:"prescriptions",    key:"rx",        width:95  },
                  { title:"Rx Dispensed",    dataIndex:"dispenses",        key:"disp",      width:110 },
                  { title:"Eye Exams",       dataIndex:"eye_examinations", key:"eye",       width:100 },
                  { title:"Lens Rx",         dataIndex:"lens_prescriptions",key:"lens",     width:85  },
                  { title:"Total ($)",       dataIndex:"total_amount",     key:"total",     width:110,
                    render:v=><Typography.Text strong>${parseFloat(v).toFixed(2)}</Typography.Text> },
                ]}
              />
            </Card>

            {/* ── Invoices Table ── */}
            <Card variant="borderless" style={{borderRadius:8}}
              title={<Typography.Text strong>Monthly Invoices</Typography.Text>}>
              <Table
                size="small"
                dataSource={invoiceRows}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No invoices generated yet"}}
                scroll={{x:1000}}
                columns={[
                  { title:"Provider", dataIndex:"provider_email", key:"provider_email", ellipsis:true },
                  { title:"Role", dataIndex:"provider_role", key:"provider_role", width:120,
                    render:v=><Tag color="blue">{v}</Tag> },
                  { title:"Month", dataIndex:"billing_month", key:"billing_month", width:100 },
                  { title:"Registrations", dataIndex:"total_patient_registrations", key:"regs", width:115 },
                  { title:"Rx Issued", dataIndex:"total_prescriptions_issued", key:"rxissued", width:90 },
                  { title:"Rx Dispensed", dataIndex:"total_prescriptions_dispensed", key:"rxdisp", width:105 },
                  { title:"Eye Exams", dataIndex:"total_eye_examinations", key:"eyeexams", width:95 },
                  { title:"Lens Rx", dataIndex:"total_lens_prescriptions", key:"lensrx", width:85 },
                  { title:"Total ($)", dataIndex:"total_amount", key:"total_amount", width:110,
                    render:v=><Typography.Text strong>${parseFloat(v).toFixed(2)}</Typography.Text> },
                  { title:"Status", dataIndex:"status", key:"status", width:100,
                    render:s=><Tag color={INVOICE_STATUS_COLOR[s]??"default"}>{s}</Tag> },
                  { title:"Actions", key:"actions", width:160, fixed:"right",
                    render:(_,r)=>(
                      <Row gutter={4}>
                        {r.status==="DRAFT"  && <Col><Button size="small" onClick={()=>handleMarkInvoiceStatus(r,"SENT")}>Mark Sent</Button></Col>}
                        {r.status==="SENT"   && <Col><Button size="small" type="primary" onClick={()=>handleMarkInvoiceStatus(r,"PAID")}>Mark Paid</Button></Col>}
                        <Col><Button size="small" onClick={()=>setInvoiceDetailTarget(r)}>Details</Button></Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </Card>
          </Spin>
        );

      case "partner-orgs": {
        const ORG_COLS = [
          { title:"Name",          dataIndex:"name",                key:"name",
            render:(n,r)=><Button type="link" style={{padding:0}} onClick={()=>openOrgDetail(r)}>{n}</Button> },
          { title:"Contact",       dataIndex:"contact_person_name", key:"contact",       ellipsis:true },
          { title:"Email",         dataIndex:"contact_email",       key:"email",         ellipsis:true },
          { title:"Members",       dataIndex:"member_count",        key:"members",       width:90 },
          { title:"Status",        dataIndex:"is_active",           key:"status",        width:90,
            render:v=>v?<Tag color="success">Active</Tag>:<Tag color="error">Inactive</Tag> },
          { title:"Contract Start",dataIndex:"contract_start_date", key:"contract_start",width:120, render:fmtDate },
          { title:"Actions",       key:"actions",                   width:120,
            render:(_,r)=>(
              <Space size={4}>
                <Button size="small" onClick={()=>openOrgDetail(r)}>Detail</Button>
                <Button size="small" icon={<EditOutlined />} onClick={()=>{
                  setOrgEditTarget(r);
                  orgEditForm.setFieldsValue({
                    contact_person_name: r.contact_person_name,
                    contact_email:       r.contact_email,
                    contact_phone:       r.contact_phone,
                    contract_end_date:   r.contract_end_date,
                    contract_notes:      r.contract_notes,
                    fee_per_registration:parseFloat(r.fee_per_registration),
                    fee_per_prescription:parseFloat(r.fee_per_prescription),
                    fee_per_dispense:    parseFloat(r.fee_per_dispense),
                  });
                  setOrgEditOpen(true);
                }}>Edit</Button>
              </Space>
            ),
          },
        ];
        const partnerOrgs = orgRows.filter(o => !o.is_owner);
        return (
          <Spin spinning={orgLoading} description="Loading…">
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Partner Organisations</Title>
            <Card variant="borderless" style={{borderRadius:8}}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setOrgCreateOpen(true)}>New Organisation</Button>}>
              <Table
                size="small"
                dataSource={partnerOrgs}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No partner organisations yet"}}
                columns={ORG_COLS}
                scroll={{x:900}}
              />
            </Card>
          </Spin>
        );
      }

      case "mchusa-org": {
        const ownerOrg = orgRows.find(o => o.is_owner);
        const mchusaUsers = userRows.filter(u => ownerOrg && u.organisation_id === ownerOrg.id);
        const roleCount = mchusaUsers.reduce((acc, u) => {
          acc[u.role] = (acc[u.role] ?? 0) + 1;
          return acc;
        }, {});
        return (
          <Spin spinning={orgLoading} description="Loading…">
            <Title level={4} style={{marginBottom:8,color:"#262626"}}>
              <Space>
                MCHUSA Organisation
                <Tag color="gold" style={{fontSize:12}}>Owner</Tag>
              </Space>
            </Title>
            {ownerOrg ? (
              <>
                <Row gutter={[16,16]} style={{marginBottom:24}}>
                  <Col xs={24} md={12}>
                    <Card variant="borderless" style={{borderRadius:8}}>
                      <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Contact">{ownerOrg.contact_person_name}</Descriptions.Item>
                        <Descriptions.Item label="Email">{ownerOrg.contact_email}</Descriptions.Item>
                        <Descriptions.Item label="Contract Start">{fmtDate(ownerOrg.contract_start_date)}</Descriptions.Item>
                        <Descriptions.Item label="Status">
                          <Tag color={ownerOrg.is_active?"success":"error"}>{ownerOrg.is_active?"Active":"Inactive"}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Billing">
                          <Tag color="green">Exempt — no billing events generated</Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>Users by Role</Text>}>
                      {Object.entries(roleCount).length === 0 ? (
                        <Text type="secondary">No users assigned yet.</Text>
                      ) : (
                        <Row gutter={[8,8]}>
                          {Object.entries(roleCount).map(([role,count])=>(
                            <Col key={role} xs={12}>
                              <Space size={6}>
                                <Tag color="blue">{role.replace(/_/g," ")}</Tag>
                                <Text strong>{count}</Text>
                              </Space>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Card>
                  </Col>
                </Row>
                <Card variant="borderless" style={{borderRadius:8}}
                  title={<Text strong>MCHUSA Direct Users ({mchusaUsers.length})</Text>}>
                  <Table
                    size="small"
                    dataSource={mchusaUsers}
                    pagination={{pageSize:15,hideOnSinglePage:true}}
                    locale={{emptyText:"No users assigned to MCHUSA"}}
                    scroll={{x:900}}
                    columns={[
                      { title:"Email",   dataIndex:"email",      key:"email",      ellipsis:true },
                      { title:"Role",    dataIndex:"role",       key:"role",       width:160, render:r=><Tag color="blue">{r?.replace(/_/g," ")}</Tag> },
                      { title:"Status",  dataIndex:"is_active",  key:"is_active",  width:100,
                        render:v=>v?<Tag color="success">Active</Tag>:<Tag color="error">Suspended</Tag> },
                      { title:"MFA",     dataIndex:"mfa_enabled",key:"mfa_enabled",width:70,  render:v=>v?<Tag color="purple">On</Tag>:<Tag>Off</Tag> },
                      { title:"Joined",  dataIndex:"created_at", key:"created_at", width:110, render:fmtDate },
                    ]}
                  />
                </Card>
                <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>Modules</Text>}>
                  <Spin spinning={orgModulesSaving}>
                    <Alert
                      type="info"
                      showIcon
                      style={{marginBottom:16}}
                      message="System Administrators, Shareholders and Operations Managers always have full access regardless of module settings. All other roles (Doctors, Patients, Pharmacists, etc.) will only see modules that are enabled here."
                    />
                    <Row gutter={[12,12]}>
                      {MODULE_DEFS.map(({key,label,icon,description,billing}) => {
                        const active = (ownerOrg.subscribed_modules ?? []).includes(key);
                        return (
                          <Col xs={24} md={12} lg={8} key={key}>
                            <Card
                              size="small"
                              style={{
                                height:"100%",
                                borderRadius:8,
                                borderLeft: active ? "4px solid #52c41a" : "4px solid #d9d9d9",
                                background: active ? "#fff" : "#fafafa",
                                transition: "border-color 0.25s, background 0.25s",
                              }}
                            >
                              <Row justify="space-between" align="top" style={{marginBottom:8}}>
                                <Space size={8}>
                                  {icon}
                                  <Text strong style={{fontSize:13}}>{label}</Text>
                                </Space>
                                {active ? (
                                  <Popconfirm
                                    title={`Disable ${label}?`}
                                    description={`MCHUSA organisation users will immediately lose access to ${label} features.`}
                                    onConfirm={()=>handleToggleModule(key, false, ownerOrg)}
                                    okText="Disable" okButtonProps={{danger:true}}
                                    cancelText="Cancel"
                                  >
                                    <Switch checked size="small" />
                                  </Popconfirm>
                                ) : (
                                  <Switch
                                    checked={false}
                                    size="small"
                                    onChange={()=>handleToggleModule(key, true, ownerOrg)}
                                  />
                                )}
                              </Row>
                              <Text type="secondary" style={{display:"block",fontSize:12,marginBottom:6}}>{description}</Text>
                              <Text style={{display:"block",fontSize:11,color:"#1677ff"}}>{billing}</Text>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Spin>
                </Card>
              </>
            ) : (
              <Alert type="info"
                message="MCHUSA organisation not found."
                description="Run the seed command or create an organisation with is_owner=true."
                showIcon />
            )}
          </Spin>
        );
      }

      case "pickup-partners": {
        const PP_COLS = [
          { title:"Name",     dataIndex:"name",                key:"name",
            render:(n,r)=><Button type="link" style={{padding:0}} onClick={()=>openPpDetail(r)}>{n}</Button> },
          { title:"Org ID",   dataIndex:"org_id",              key:"org_id",   width:110, render:v=><Text code>{v}</Text> },
          { title:"Type",     dataIndex:"partner_type",        key:"type",     width:150,
            render:v=><Tag color="purple">{v?.replace(/_/g," ")}</Tag> },
          { title:"Active",   dataIndex:"is_active",           key:"active",   width:90,
            render:v=>v?<Tag color="success">Active</Tag>:<Tag color="error">Inactive</Tag> },
          { title:"Reg. Fee", dataIndex:"registration_fee_paid",key:"fee",     width:100,
            render:v=>v?<Tag color="green">Paid</Tag>:<Tag>Unpaid</Tag> },
          { title:"Contact",  dataIndex:"contact_email",       key:"email",    ellipsis:true },
          { title:"Registered",dataIndex:"registration_date",  key:"reg",      width:120, render:fmtDate },
          { title:"Actions",  key:"actions",                   width:160,
            render:(_,r)=>(
              <Space size={4}>
                <Button size="small" onClick={()=>openPpDetail(r)}>Detail</Button>
                <Button size="small" icon={<EditOutlined />} onClick={()=>{
                  setPpEditTarget(r);
                  ppEditForm.setFieldsValue({
                    name:                  r.name,
                    contact_email:         r.contact_email,
                    contact_phone:         r.contact_phone,
                    notes:                 r.notes,
                    registration_fee_paid: r.registration_fee_paid,
                    is_active:             r.is_active,
                  });
                  setPpEditOpen(true);
                }}>Edit</Button>
                <Button size="small" danger={r.is_active}
                  onClick={()=>handlePpToggleActive(r)}>
                  {r.is_active ? "Deactivate" : "Activate"}
                </Button>
              </Space>
            ) },
        ];
        return (
          <Spin spinning={ppLoading}>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Pickup Partners</Title>
            <Alert type="info" showIcon style={{marginBottom:16,borderRadius:8}}
              message="Pickup and delivery partner organisations are registered and managed here. These are third-party service providers authorised to collect and deliver medications on behalf of patients. Partners registered here will appear as a pickup option on patient prescriptions." />
            <Card variant="borderless" style={{borderRadius:8}}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setPpCreateOpen(true)}>New Pickup Partner</Button>}>
              <Table size="small" dataSource={ppRows}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No pickup partners yet"}}
                columns={PP_COLS} scroll={{x:1000}} />
            </Card>

            {/* Create modal */}
            <Modal title="Create Pickup Partner Organisation"
              open={ppCreateOpen}
              onOk={()=>ppCreateForm.submit()}
              onCancel={()=>{setPpCreateOpen(false);ppCreateForm.resetFields();}}
              okText="Create" confirmLoading={ppSubmitting} destroyOnHidden width={560}>
              <Alert type="info" showIcon style={{marginBottom:16}}
                message="A DHN login account will be created automatically. Share the generated credentials with the partner." />
              <Form form={ppCreateForm} layout="vertical" onFinish={handlePpCreate}>
                <Row gutter={12}>
                  <Col span={14}>
                    <Form.Item name="name" label="Organisation Name"
                      rules={[{required:true,message:"Required"}]}>
                      <Input placeholder="e.g. TunzaCare" />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item name="org_id" label="Org ID"
                      rules={[{required:true,message:"Required"}]}>
                      <Input placeholder="e.g. TC-0001" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="partner_type" label="Partner Type" initialValue="PICKUP_DELIVERY">
                  <Select>
                    <Option value="PICKUP_DELIVERY">Pickup &amp; Delivery Service</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="contact_email" label="Portal Login Email"
                  rules={[{required:true,type:"email",message:"Valid email required"}]}>
                  <Input placeholder="Partner's DHN login email" />
                </Form.Item>
                <Form.Item name="contact_phone" label="Contact Phone">
                  <Input placeholder="+63 900 000 0000" />
                </Form.Item>
                <Form.Item name="notes" label="Notes (optional)">
                  <Input.TextArea rows={3} placeholder="Contract details, notes for MCHUSA staff..." />
                </Form.Item>
              </Form>
            </Modal>

            {/* Creation success modal */}
            <Modal title="Partner Organisation Created"
              open={Boolean(ppCreateResult)}
              onOk={()=>setPpCreateResult(null)}
              onCancel={()=>setPpCreateResult(null)}
              okText="Done" cancelButtonProps={{style:{display:"none"}}} destroyOnHidden>
              {ppCreateResult && (
                <div>
                  <Alert type="success" showIcon style={{marginBottom:16}}
                    message={`${ppCreateResult.name} has been created successfully.`} />
                  <Alert type="warning" showIcon style={{marginBottom:16}}
                    message="Share these login credentials with the partner. The password will not be shown again." />
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Org ID"><Text code>{ppCreateResult.org_id}</Text></Descriptions.Item>
                    <Descriptions.Item label="Login Email"><Text copyable>{ppCreateResult.login_email}</Text></Descriptions.Item>
                    <Descriptions.Item label="Generated Password">
                      <Text code copyable>{ppCreateResult.generated_password}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Active"><Tag color={ppCreateResult.is_active?"success":"error"}>{ppCreateResult.is_active?"Yes":"No"}</Tag></Descriptions.Item>
                  </Descriptions>
                </div>
              )}
            </Modal>

            {/* Edit modal */}
            <Modal title="Edit Pickup Partner"
              open={ppEditOpen}
              onOk={()=>ppEditForm.submit()}
              onCancel={()=>{setPpEditOpen(false);ppEditForm.resetFields();setPpEditTarget(null);}}
              okText="Save" confirmLoading={ppSubmitting} destroyOnHidden>
              <Form form={ppEditForm} layout="vertical" onFinish={handlePpEdit}>
                <Form.Item name="name" label="Organisation Name"
                  rules={[{required:true,message:"Required"}]}>
                  <Input />
                </Form.Item>
                <Form.Item name="contact_email" label="Contact Email">
                  <Input />
                </Form.Item>
                <Form.Item name="contact_phone" label="Contact Phone">
                  <Input />
                </Form.Item>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={3} />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="registration_fee_paid" valuePropName="checked" label="Registration Fee Paid">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="is_active" valuePropName="checked" label="Active">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Detail drawer */}
            <Modal title={ppDetailTarget ? `${ppDetailTarget.name} (${ppDetailTarget.org_id})` : "Detail"}
              open={Boolean(ppDetailTarget)}
              onOk={()=>setPpDetailTarget(null)}
              onCancel={()=>{setPpDetailTarget(null);setPpDetailBilling(null);}}
              okText="Close" cancelButtonProps={{style:{display:"none"}}}
              destroyOnHidden width={720}>
              {ppDetailTarget && (
                <Spin spinning={ppDetailLoading}>
                  <Descriptions column={2} bordered size="small" style={{marginBottom:16}}>
                    <Descriptions.Item label="Org ID"><Text code>{ppDetailTarget.org_id}</Text></Descriptions.Item>
                    <Descriptions.Item label="Type">{ppDetailTarget.partner_type?.replace(/_/g," ")}</Descriptions.Item>
                    <Descriptions.Item label="Login Email">{ppDetailTarget.account_email ?? ppDetailTarget.contact_email}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{ppDetailTarget.contact_phone || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={ppDetailTarget.is_active?"success":"error"}>{ppDetailTarget.is_active?"Active":"Inactive"}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Reg. Fee">
                      <Tag color={ppDetailTarget.registration_fee_paid?"green":"warning"}>
                        {ppDetailTarget.registration_fee_paid?"Paid":"Unpaid"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Registered">{fmtDate(ppDetailTarget.registration_date)}</Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>{ppDetailTarget.notes || "—"}</Descriptions.Item>
                  </Descriptions>
                  <Text strong style={{display:"block",marginBottom:8}}>Billing Events</Text>
                  <Table size="small"
                    dataSource={(ppDetailBilling ?? []).map(r=>({...r,key:r.id}))}
                    pagination={{pageSize:10,hideOnSinglePage:true}}
                    locale={{emptyText:"No billing events yet"}}
                    columns={[
                      {title:"Date",        dataIndex:"created_at",     key:"date",   width:120, render:fmtDate},
                      {title:"Patient",     dataIndex:"patient_email",  key:"patient",ellipsis:true},
                      {title:"Fee",         dataIndex:"fee_amount",     key:"fee",    width:100, render:a=>`₱${parseFloat(a).toFixed(2)}`},
                      {title:"Invoice Ref", dataIndex:"invoice_ref",    key:"ref",    width:140,
                        render:r=>r||<Text type="secondary">Pending</Text>},
                    ]}
                    summary={rows=>{
                      const total = rows.reduce((sum,r)=>sum+parseFloat(r.fee_amount||0),0);
                      return rows.length>0 && (
                        <Table.Summary.Row style={{background:"#fafafa"}}>
                          <Table.Summary.Cell index={0} colSpan={2}><Text strong>Total</Text></Table.Summary.Cell>
                          <Table.Summary.Cell index={2}><Text strong>₱{total.toFixed(2)}</Text></Table.Summary.Cell>
                          <Table.Summary.Cell index={3} />
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </Spin>
              )}
            </Modal>
          </Spin>
        );
      }

      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Email">{user?.email ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Role"><Tag color="red">SYSTEM_ADMIN</Tag></Descriptions.Item>
              </Descriptions>
            </Card>
            <ChangePasswordForm />
          </>
        );

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <Layout style={{minHeight:"100vh"}}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{background:"#fff",borderRight:"1px solid #f0f0f0"}}>
        <div style={{height:64,display:"flex",alignItems:"center",justifyContent:"center",
          borderBottom:"1px solid #f0f0f0",padding:"0 16px"}}>
          {!collapsed && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",lineHeight:1.3}}>
              <Text strong style={{color:"#1677ff",fontSize:13,whiteSpace:"nowrap"}}>Digital Healthcare Network</Text>
              <Space size={4}>
                <Text style={{color:"#1677ff",fontSize:12,whiteSpace:"nowrap"}}>Admin</Text>
                <Tag color="gold" style={{marginInlineEnd:0,fontSize:11,padding:"0 5px"}}>Owner</Tag>
              </Space>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={MENU_ITEMS}
          onClick={({key})=>setSelectedMenu(key)}
          style={{border:"none",marginTop:8}} />
      </Sider>

      <Layout>
        <Header style={{background:"#fff",padding:"0 24px",display:"flex",alignItems:"center",
          justifyContent:"space-between",borderBottom:"1px solid #f0f0f0",position:"sticky",top:0,zIndex:100}}>
          <div>
            <Text strong style={{fontSize:16,color:"#1677ff"}}>MCHUSA</Text>
            <Text style={{fontSize:14,color:"#8c8c8c",marginLeft:8}}>| Digital Healthcare Network</Text>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <AdminNotificationBell onNavigate={(section) => setSelectedMenu(section)} />
            <Dropdown menu={{items:userMenuItems}} placement="bottomRight" arrow>
              <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <Avatar size={32} icon={<UserOutlined />} style={{background:"#1677ff"}} />
                <Text style={{fontSize:14,color:"#262626"}}>{user.email??"Admin"}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{padding:24,background:"#f5f7fa"}}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3].map(i => (
                  <Col xs={24} sm={12} lg={8} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 24 }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── Add User Modal ── */}
      <Modal title="Add New User" open={addUserOpen}
        onOk={()=>addUserForm.submit()}
        onCancel={()=>{setAddUserOpen(false);setAddUserRole("");addUserForm.resetFields();}}
        okText="Create User" confirmLoading={submitting} destroyOnHidden keyboard={true}
        width={540}>
        <Form form={addUserForm} layout="vertical" onFinish={handleAddUser} initialValues={{is_active:true}}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{required:true,message:"Required"}]}>
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{required:true,message:"Required"}]}>
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email" rules={[{required:true,message:"Required"},{type:"email",message:"Invalid email"}]}>
            <Input placeholder="user@mchusa.com" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{required:true,message:"Select a role"}]}>
            <Select placeholder="Select role" onChange={v => setAddUserRole(v)}>
              {DHN_ROLES.map(r=><Option key={r} value={r}>{r.replace(/_/g," ")}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{required:true,message:"Required"},{min:8,message:"Min 8 characters"}]}>
            <Input.Password placeholder="Password" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>

          {/* ── Pharmacy fields — only shown when PHARMACIST is selected ── */}
          {addUserRole === "PHARMACIST" && (
            <>
              <Divider style={{margin:"12px 0 16px"}}>
                <span style={{fontSize:12,color:"#8c8c8c"}}>Pharmacy Profile</span>
              </Divider>
              <Alert type="info" showIcon style={{marginBottom:14,fontSize:12}}
                message="The pharmacy profile will be created automatically with the account." />
              <Form.Item name="pharmacy_name" label="Pharmacy Name"
                rules={[{required:true,message:"Required for pharmacist accounts"}]}>
                <Input placeholder="e.g. City Central Pharmacy" />
              </Form.Item>
              <Form.Item name="license_number" label="License Number"
                rules={[{required:true,message:"Required for pharmacist accounts"}]}>
                <Input placeholder="Pharmacy license number" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="pharmacy_address" label="Pharmacy Address">
                    <Input placeholder="Street, City" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="pharmacy_phone" label="Pharmacy Phone">
                    <Input placeholder="+63 900 000 0000" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="pharmacy_is_verified" label="Mark as Verified" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* ── New Ticket Modal ── */}
      <Modal title="New Support Ticket" open={newTicketOpen} onOk={()=>newTicketForm.submit()}
        onCancel={()=>{setNewTicketOpen(false);newTicketForm.resetFields();}}
        okText="Submit" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={newTicketForm} layout="vertical" onFinish={handleNewTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="CRITICAL">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Detailed description of the issue" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Respond to Ticket Modal ── */}
      <Modal title={`Respond to Ticket — ${respondTarget?.subject ?? ""}`}
        open={respondOpen}
        onOk={()=>respondForm.submit()}
        onCancel={()=>{ setRespondOpen(false); respondForm.resetFields(); setRespondTarget(null); }}
        okText="Submit Response" confirmLoading={submitting} destroyOnHidden width={560}
 keyboard>
        {respondTarget && (
          <div style={{marginBottom:16,padding:12,background:"#f5f5f5",borderRadius:6}}>
            <Text type="secondary" style={{fontSize:12}}>From: </Text>
            <Text style={{fontSize:12}}>{respondTarget.submitted_by_email}</Text>
            <Text type="secondary" style={{fontSize:12,marginLeft:12}}>Priority: </Text>
            <Tag color={PRIORITY_COLOR[respondTarget.priority]}>{respondTarget.priority}</Tag>
            <div style={{marginTop:8,fontSize:13,color:"#595959"}}>{respondTarget.description}</div>
          </div>
        )}
        <Form form={respondForm} layout="vertical" onFinish={handleRespond}>
          <Form.Item name="status" label="Update Status" rules={[{required:true,message:"Required"}]}>
            <Select>
              <Option value="OPEN">Open</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="RESOLVED">Resolved</Option>
              <Option value="CLOSED">Closed</Option>
            </Select>
          </Form.Item>
          <Form.Item name="resolution_notes" label="Response to Partner / Notes"
            tooltip="This response is visible to the ops manager who submitted the ticket.">
            <Input.TextArea rows={4} placeholder="Describe the resolution or next steps…" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)} footer={
          <Space>
            {ticketDetail && ticketDetail.status !== "CLOSED" && (
              <Button type="primary" onClick={()=>{
                setRespondTarget(ticketDetail);
                respondForm.setFieldsValue({
                  status: ticketDetail.status === "ESCALATED" ? "RESOLVED" : ticketDetail.status,
                  resolution_notes: ticketDetail.resolution_notes ?? "",
                });
                setTicketDetail(null);
                setRespondOpen(true);
              }}>Respond</Button>
            )}
            <Button onClick={()=>setTicketDetail(null)}>Close</Button>
          </Space>
        } destroyOnHidden keyboard width={560}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",      ticketDetail.subject],
                  ["Organisation", ticketDetail.organisation_name
                    ? <Tag color="blue">{ticketDetail.organisation_name}</Tag>
                    : <Text type="secondary">MCHUSA Internal</Text>],
                  ["Submitted By", ticketDetail.submitted_by_email ?? "—"],
                  ["Category",     ticketDetail.category?.replace(/_/g," ") ?? "—"],
                  ["Priority",     <Tag color={PRIORITY_COLOR[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",       <Tag color={TICKET_STATUS_COLOR[ticketDetail.status]}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",      fmt(ticketDetail.created_at)],
                  ["Updated",      fmt(ticketDetail.updated_at)],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>
                {ticketDetail.description}
              </div>
            </div>
            {(ticketDetail.status === "ESCALATED" || ticketDetail.ticket_level === 3) && (
              <div style={{marginTop:12,padding:12,background:"#fff1f0",border:"1px solid #ffa39e",borderRadius:4}}>
                <Text strong style={{color:"#cf1322",display:"block",marginBottom:6}}>
                  Escalation Chain
                </Text>
                <table style={{fontSize:12,borderCollapse:"collapse",width:"100%"}}>
                  <tbody>
                    <tr>
                      <td style={{color:"#8c8c8c",paddingRight:12,paddingBottom:4,whiteSpace:"nowrap"}}>Original user:</td>
                      <td style={{fontWeight:500}}>{ticketDetail.submitted_by_email ?? "—"}</td>
                    </tr>
                    {ticketDetail.organisation_name && (
                      <tr>
                        <td style={{color:"#8c8c8c",paddingRight:12,paddingBottom:4,whiteSpace:"nowrap"}}>Organisation:</td>
                        <td><Tag color="blue" style={{fontSize:11}}>{ticketDetail.organisation_name}</Tag></td>
                      </tr>
                    )}
                    {ticketDetail.escalated_by_email && (
                      <tr>
                        <td style={{color:"#8c8c8c",paddingRight:12,paddingBottom:4,whiteSpace:"nowrap"}}>Escalated by:</td>
                        <td style={{fontWeight:500}}>
                          {ticketDetail.escalated_by_email}
                          {ticketDetail.escalated_at && <Text type="secondary" style={{marginLeft:6,fontSize:11}}>· {fmtDate(ticketDetail.escalated_at)}</Text>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {ticketDetail.escalation_note && (
                  <div style={{marginTop:6,padding:"6px 8px",background:"rgba(255,255,255,0.7)",borderRadius:4,fontSize:13,color:"#595959"}}>
                    <Text strong style={{fontSize:12,display:"block",marginBottom:2}}>Escalation note:</Text>
                    {ticketDetail.escalation_note}
                  </div>
                )}
              </div>
            )}
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Admin Response</Text>
                {ticketDetail.responded_by_email && (
                  <Text type="secondary" style={{fontSize:11,display:"block",marginBottom:4}}>
                    By {ticketDetail.responded_by_email}
                    {ticketDetail.responded_at && ` · ${fmtDate(ticketDetail.responded_at)}`}
                  </Text>
                )}
                <div style={{marginTop:4,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>
                  {ticketDetail.resolution_notes}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Add / Edit Config Modal ── */}
      <Modal
        title={configEditTarget ? "Edit Configuration" : "Add Configuration"}
        open={configModalOpen}
        onOk={() => configForm.submit()}
        onCancel={() => { setConfigModalOpen(false); configForm.resetFields(); }}
        okText={configEditTarget ? "Save Changes" : "Add"}
        confirmLoading={submitting}
        destroyOnHidden keyboard>
        <Form form={configForm} layout="vertical" onFinish={handleSaveConfig}>
          <Form.Item name="key" label="Key"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Unique identifier, e.g. MAX_PRESCRIPTIONS_PER_DAY">
            <Input
              placeholder="e.g. MAX_PRESCRIPTIONS_PER_DAY"
              disabled={Boolean(configEditTarget)}
            />
          </Form.Item>
          <Form.Item name="value" label="Value"
            rules={[{ required: true, message: "Required" }]}
            tooltip="Do not store secrets here — use environment variables for credentials">
            <Input placeholder="e.g. 10" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="What this setting controls" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Fee Modal ── */}
      <Modal title="Edit Fee Configuration" open={editFeeOpen}
        onOk={()=>editFeeForm.submit()}
        onCancel={()=>{setEditFeeOpen(false);setEditFeeTarget(null);editFeeForm.resetFields();}}
        okText="Save" confirmLoading={submitting} destroyOnHidden keyboard>
        <Form form={editFeeForm} layout="vertical" onFinish={handleEditFee}>
          <Form.Item name="amount" label="Amount ($)" rules={[{required:true,message:"Required"}]}>
            <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Add Fee Modal ── */}
      <Modal title="Add Fee Configuration" open={addFeeOpen}
        onOk={()=>addFeeForm.submit()}
        onCancel={()=>{setAddFeeOpen(false);addFeeForm.resetFields();}}
        okText="Add" confirmLoading={submitting} destroyOnHidden keyboard>
        <Form form={addFeeForm} layout="vertical" onFinish={handleEditFee}>
          <Form.Item name="fee_type" label="Fee Type" rules={[{required:true,message:"Required"}]}>
            <Select placeholder="Select fee type">
              {Object.entries(FEE_TYPE_LABEL).map(([v,l])=>(
                <Option key={v} value={v}>{l}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Amount ($)" rules={[{required:true,message:"Required"}]}>
            <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Invoice Detail Modal ── */}
      <Modal title="Invoice Details" open={Boolean(invoiceDetailTarget)}
        onCancel={()=>setInvoiceDetailTarget(null)}
        footer={
          invoiceDetailTarget ? (
            <Row justify="end" gutter={8}>
              {invoiceDetailTarget.status==="DRAFT" && (
                <Col><Button onClick={()=>handleMarkInvoiceStatus(invoiceDetailTarget,"SENT")}>Mark as Sent</Button></Col>
              )}
              {invoiceDetailTarget.status==="SENT" && (
                <Col><Button type="primary" onClick={()=>handleMarkInvoiceStatus(invoiceDetailTarget,"PAID")}>Mark as Paid</Button></Col>
              )}
              <Col><Button onClick={()=>setInvoiceDetailTarget(null)}>Close</Button></Col>
            </Row>
          ) : <Button onClick={()=>setInvoiceDetailTarget(null)}>Close</Button>
        }
 keyboard destroyOnHidden width={560}>
        {invoiceDetailTarget && (
          <Row gutter={[16,16]}>
            <Col span={24}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <tbody>
                  {[
                    ["Provider",      invoiceDetailTarget.provider_email],
                    ["Role",          <Tag color="blue">{invoiceDetailTarget.provider_role}</Tag>],
                    ["Billing Month", invoiceDetailTarget.billing_month],
                    ["Status",        <Tag color={INVOICE_STATUS_COLOR[invoiceDetailTarget.status]}>{invoiceDetailTarget.status}</Tag>],
                    ["Patient Registrations", invoiceDetailTarget.total_patient_registrations],
                    ["Registration Fee", `$${parseFloat(invoiceDetailTarget.patient_registration_fee).toFixed(2)}`],
                    ["Prescriptions Issued", invoiceDetailTarget.total_prescriptions_issued],
                    ["Rx Issued Fee", `$${parseFloat(invoiceDetailTarget.prescription_issued_fee).toFixed(2)}`],
                    ["Prescriptions Dispensed", invoiceDetailTarget.total_prescriptions_dispensed],
                    ["Rx Dispensed Fee", `$${parseFloat(invoiceDetailTarget.prescription_dispensed_fee).toFixed(2)}`],
                    ["Total Amount", <Typography.Text strong>${parseFloat(invoiceDetailTarget.total_amount).toFixed(2)}</Typography.Text>],
                    ["Generated At", fmtDate(invoiceDetailTarget.generated_at)],
                    ["Paid At", invoiceDetailTarget.paid_at ? fmtDate(invoiceDetailTarget.paid_at) : "—"],
                  ].map(([label,val])=>(
                    <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                      <td style={{padding:"8px 12px",color:"#8c8c8c",width:"45%"}}>{label}</td>
                      <td style={{padding:"8px 12px"}}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Col>
          </Row>
        )}
      </Modal>
      {/* ── Create Organisation Modal ── */}
      <Modal title="New Partner Organisation" open={orgCreateOpen}
        onOk={()=>orgCreateForm.submit()}
        onCancel={()=>{setOrgCreateOpen(false);orgCreateForm.resetFields();}}
        okText="Create Organisation" confirmLoading={submitting} destroyOnHidden keyboard width={600}>
        <Form form={orgCreateForm} layout="vertical" onFinish={handleCreateOrg}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="name" label="Company Name" rules={[{required:true}]}>
                <Input placeholder="e.g. Caribbean Health Partners" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_person_name" label="Contact Person" rules={[{required:true}]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contact_email" label="Contact Email" rules={[{required:true},{type:"email"}]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_phone" label="Contact Phone">
                <Input placeholder="+1-868-555-0100" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contract_start_date" label="Contract Start Date" rules={[{required:true}]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contract_end_date" label="Contract End Date (optional)">
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="contract_notes" label="Contract Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="fee_per_registration" label="Fee / Registration ($)" rules={[{required:true}]}>
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fee_per_prescription" label="Fee / Prescription ($)" rules={[{required:true}]}>
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fee_per_dispense" label="Fee / Dispense ($)" rules={[{required:true}]}>
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ops_manager_email" label="Ops Manager Email (optional)"
            tooltip="Leave blank to auto-generate ops@{slug}.mchusa-partner.com">
            <Input placeholder="ops@company.com" />
          </Form.Item>
          <Form.Item
            name="subscribed_modules"
            label="Subscribed Modules"
            initialValue={["DOCTOR","PATIENT","PHARMACY"]}
            rules={[{required:true,type:"array",min:1,message:"Select at least one module"}]}>
            <Checkbox.Group style={{display:"flex",flexDirection:"column",gap:6}}>
              {[
                {value:"DOCTOR",      label:"Doctor — portal, enrollment, diagnoses, prescriptions"},
                {value:"PATIENT",     label:"Patient — portal, medical history, prescription tracking"},
                {value:"PHARMACY",    label:"Pharmacy — prescription queue, dispensing, inventory"},
                {value:"OPHTHALMOLOGY",label:"Ophthalmology & Optician — eye exams, lens prescriptions"},
                {value:"EMS",         label:"EMS — emergency dispatch, ambulance coordination"},
                {value:"BILLING",     label:"Billing & Claims — insurance submissions, government billing"},
              ].map(({value,label})=>(
                <Checkbox key={value} value={value}>{label}</Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Org Created Success Modal ── */}
      <Modal title="Organisation Created" open={Boolean(orgCreateResult)}
        onCancel={()=>setOrgCreateResult(null)}
        footer={<Button type="primary" onClick={()=>setOrgCreateResult(null)}>Done</Button>}
        keyboard destroyOnHidden>
        {orgCreateResult && (
          <div>
            <Alert type="success" showIcon style={{marginBottom:16}}
              message={`Organisation "${orgCreateResult.organisation?.name}" created successfully.`} />
            <Typography.Text strong>Operations Manager Credentials</Typography.Text>
            <table style={{width:"100%",borderCollapse:"collapse",marginTop:8}}>
              <tbody>
                {[
                  ["Email",    orgCreateResult.ops_manager?.email],
                  ["Password", orgCreateResult.ops_manager?.temp_password],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>
                      <Typography.Text copyable style={{fontFamily:"monospace"}}>{val}</Typography.Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Alert type="warning" showIcon style={{marginTop:12}}
              message="Share these credentials securely with the partner. The password will not be shown again." />
          </div>
        )}
      </Modal>

      {/* ── Edit Organisation Modal ── */}
      <Modal title={`Edit — ${orgEditTarget?.name ?? ""}`} open={orgEditOpen}
        onOk={()=>orgEditForm.submit()}
        onCancel={()=>{setOrgEditOpen(false);orgEditForm.resetFields();setOrgEditTarget(null);}}
        okText="Save Changes" confirmLoading={submitting} destroyOnHidden keyboard width={560}>
        <Form form={orgEditForm} layout="vertical" onFinish={handleEditOrg}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contact_person_name" label="Contact Person" rules={[{required:true}]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_email" label="Contact Email" rules={[{required:true},{type:"email"}]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="contact_phone" label="Contact Phone">
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contract_end_date" label="Contract End Date">
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="contract_notes" label="Contract Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="fee_per_registration" label="Fee / Registration ($)">
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fee_per_prescription" label="Fee / Prescription ($)">
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fee_per_dispense" label="Fee / Dispense ($)">
                <InputNumber style={{width:"100%"}} min={0} precision={2} step={0.01} prefix="$" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Organisation Detail Modal ── */}
      <Modal
        title={
          orgDetailTarget ? (
            <Space size={8}>
              <span>{orgDetailTarget.name}</span>
              <Tag color={orgDetailTarget.is_active ? "success" : "error"} style={{marginBottom:0}}>
                {orgDetailTarget.is_active ? "Active" : "Inactive"}
              </Tag>
            </Space>
          ) : "Organisation Detail"
        }
        open={Boolean(orgDetailTarget)}
        onCancel={()=>{ setOrgDetailTarget(null); setOrgBillingData(null); }}
        footer={null}
        width={900}
        centered
        destroyOnHidden
        maskClosable
      >
        {orgDetailLoading ? (
          <Skeleton active paragraph={{rows:8}} />
        ) : orgDetailStats ? (
          <div style={{maxHeight:"75vh", overflowY:"auto", paddingRight:4}}>
            <Tabs defaultActiveKey="overview" items={[
              {
                key: "overview",
                label: "Overview",
                children: (
                  <Row gutter={32}>
                    {/* Left — org details + actions */}
                    <Col xs={24} md={12}>
                      <Descriptions column={1} bordered size="small" style={{marginBottom:16}}>
                        <Descriptions.Item label="Contact">{orgDetailTarget?.contact_person_name}</Descriptions.Item>
                        <Descriptions.Item label="Email">{orgDetailTarget?.contact_email}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{orgDetailTarget?.contact_phone || "—"}</Descriptions.Item>
                        <Descriptions.Item label="Contract Start">{fmtDate(orgDetailTarget?.contract_start_date)}</Descriptions.Item>
                        {orgDetailTarget?.contract_end_date && (
                          <Descriptions.Item label="Contract End">{fmtDate(orgDetailTarget.contract_end_date)}</Descriptions.Item>
                        )}
                      </Descriptions>

                      <Card size="small" style={{marginBottom:16,background:"#fff7e6",border:"1px solid #ffd591"}}>
                        <Row gutter={8}>
                          <Col span={12}><Text type="secondary" style={{fontSize:12}}>This Month</Text><br />
                            <Text strong style={{fontSize:18}}>${parseFloat(orgDetailStats.total_fees_this_month ?? 0).toFixed(2)}</Text>
                          </Col>
                          <Col span={12}><Text type="secondary" style={{fontSize:12}}>All Time</Text><br />
                            <Text strong style={{fontSize:18}}>${parseFloat(orgDetailStats.total_fees_all_time ?? 0).toFixed(2)}</Text>
                          </Col>
                        </Row>
                      </Card>

                      <Space wrap>
                        <Button icon={<EditOutlined />} onClick={()=>{
                          setOrgEditTarget(orgDetailTarget);
                          orgEditForm.setFieldsValue({
                            contact_person_name: orgDetailTarget.contact_person_name,
                            contact_email:       orgDetailTarget.contact_email,
                            contact_phone:       orgDetailTarget.contact_phone,
                            contract_end_date:   orgDetailTarget.contract_end_date,
                            fee_per_registration: parseFloat(orgDetailStats.fee_per_registration),
                            fee_per_prescription: parseFloat(orgDetailStats.fee_per_prescription),
                            fee_per_dispense:     parseFloat(orgDetailStats.fee_per_dispense),
                          });
                          setOrgEditOpen(true);
                        }}>Edit</Button>
                        {orgDetailTarget?.is_active && (
                          <Popconfirm
                            title={`Deactivate ${orgDetailTarget.name}?`}
                            description="All users in this organisation will be deactivated."
                            onConfirm={()=>handleDeactivateOrg(orgDetailTarget)}
                            okText="Deactivate" okButtonProps={{danger:true}}>
                            <Button danger>Deactivate Organisation</Button>
                          </Popconfirm>
                        )}
                      </Space>
                    </Col>

                    {/* Right — user stats */}
                    <Col xs={24} md={12}>
                      <Text type="secondary" style={{display:"block",marginBottom:10,fontSize:13}}>User Breakdown</Text>
                      <Row gutter={[12,12]}>
                        {[
                          {label:"Total Users", value:orgDetailStats.total_registered_users, color:"#1677ff"},
                          {label:"Patients",    value:orgDetailStats.patients,               color:"#52c41a"},
                          {label:"Doctors",     value:orgDetailStats.doctors,                color:"#722ed1"},
                          {label:"Pharmacists", value:orgDetailStats.pharmacists,            color:"#fa8c16"},
                        ].map(({label,value,color})=>(
                          <Col span={12} key={label}>
                            <Card size="small" variant="borderless" style={{background:"#f5f7fa",borderRadius:6,textAlign:"center"}}>
                              <div style={{fontSize:26,fontWeight:700,color}}>{value}</div>
                              <div style={{fontSize:12,color:"#8c8c8c"}}>{label}</div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "billing",
                label: "Billing",
                children: (() => {
                  const billingYear  = orgBillingMonth ? orgBillingMonth.year()      : new Date().getFullYear();
                  const billingMonth = orgBillingMonth ? orgBillingMonth.month() + 1 : new Date().getMonth() + 1;
                  const MONTH_NAMES  = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
                  const billingLabel = `${MONTH_NAMES[billingMonth]} ${billingYear}`;
                  const INVOICE_STATUS_BADGE = {
                    DRAFT:   {color:"default", text:"Draft"},
                    SENT:    {color:"blue",    text:"Sent"},
                    VIEWED:  {color:"purple",  text:"Viewed"},
                    PAID:    {color:"success", text:"Paid"},
                    OVERDUE: {color:"error",   text:"Overdue"},
                  };
                  const existingInvoice = orgInvoices.find(
                    inv => inv.period_month === billingMonth && inv.period_year === billingYear
                  );
                  const alreadyInvoiced = Boolean(existingInvoice);
                  const invoiceTagBadge = INVOICE_STATUS_BADGE[existingInvoice?.status] ?? {color:"blue",text:"Sent"};

                  // Filter billing line items: hide ophthalmology lines if org not subscribed
                  const orgMods = orgDetailTarget?.subscribed_modules ?? [];
                  const hasOphthalmology = orgMods.length === 0 || orgMods.includes("OPHTHALMOLOGY");
                  const visibleLineItems = (orgBillingData?.line_items ?? [])
                    .filter(li => {
                      if (!hasOphthalmology && li.event_type === "EYE_EXAMINATION")   return false;
                      if (!hasOphthalmology && li.event_type === "LENS_PRESCRIPTION") return false;
                      return true;
                    })
                    .map((li, i) => ({...li, key: i}));

                  return (
                    <Spin spinning={orgBillingLoading}>
                      {/* Controls row */}
                      <Row gutter={8} align="middle" justify="space-between" style={{marginBottom:16}}>
                        <Col>
                          <Space>
                            <DatePicker picker="month" format="YYYY-MM"
                              value={orgBillingMonth}
                              onChange={v=>{ setOrgBillingMonth(v); loadOrgBilling(v?v.format("YYYY-MM"):null); }}
                              placeholder="Current month" allowClear
                              onClear={()=>{ setOrgBillingMonth(null); loadOrgBilling(null); }}
                            />
                            <Button icon={<DownloadOutlined />} disabled={!orgBillingData?.events?.length}
                              onClick={exportBillingCSV}>
                              Export CSV
                            </Button>
                          </Space>
                        </Col>
                        <Col>
                          {alreadyInvoiced ? (
                            <Tag
                              color={invoiceTagBadge.color}
                              style={{fontSize:13, padding:"4px 10px"}}
                            >
                              Invoice: {invoiceTagBadge.text} — {billingLabel}
                            </Tag>
                          ) : (
                            <Popconfirm
                              title={`Generate and send ${billingLabel} invoice to ${orgDetailTarget?.name}?`}
                              description={
                                <div>
                                  <div>Total amount: <strong>${parseFloat(orgBillingData?.total_amount ?? 0).toFixed(2)}</strong></div>
                                  {(orgDetailTarget?.subscribed_modules ?? []).length > 0 && (
                                    <div style={{marginTop:4}}>
                                      Modules: <strong>{orgDetailTarget.subscribed_modules.map(m=>m.charAt(0)+m.slice(1).toLowerCase()).join(", ")}</strong>
                                    </div>
                                  )}
                                  <div style={{color:"#8c8c8c",fontSize:12,marginTop:4}}>The Operations Manager will be notified immediately.</div>
                                </div>
                              }
                              onConfirm={() => handleGenerateOrgInvoice(billingMonth, billingYear)}
                              okText="Generate & Send Invoice"
                              cancelText="Cancel"
                              disabled={!orgBillingData}
                            >
                              <Button
                                type="primary"
                                icon={<FileTextOutlined />}
                                loading={invoiceGenLoading}
                                disabled={!orgBillingData}
                              >
                                Generate & Send Invoice for {billingLabel}
                              </Button>
                            </Popconfirm>
                          )}
                        </Col>
                      </Row>

                      {orgBillingData && (
                        <>
                          {/* Line-item summary */}
                          <div style={{marginBottom:16}}>
                            <Text strong style={{display:"block",marginBottom:8,fontSize:14}}>
                              {orgBillingData.month} — {orgBillingData.event_count} events
                            </Text>
                            <Table
                              size="small"
                              dataSource={visibleLineItems}
                              pagination={false}
                              style={{background:"#fff"}}
                              columns={[
                                {title:"Service",   dataIndex:"label",    key:"label"},
                                {title:"Count",     dataIndex:"count",    key:"count",   width:80,  align:"right"},
                                {title:"Rate ($)",  dataIndex:"rate",     key:"rate",    width:150, align:"right",
                                  render:(v, r) => r.is_custom
                                    ? <span><Text style={{color:"#1677ff"}}>${parseFloat(v).toFixed(2)}</Text>{" "}<Tag color="blue" style={{fontSize:10,padding:"0 4px"}}>custom</Tag></span>
                                    : `$${parseFloat(v).toFixed(2)}`},
                                {title:"Subtotal",  dataIndex:"subtotal", key:"subtotal",width:120, align:"right",
                                  render:v=><Text strong>${parseFloat(v).toFixed(2)}</Text>},
                              ]}
                              summary={()=>(
                                <Table.Summary.Row>
                                  <Table.Summary.Cell index={0} colSpan={3} align="right">
                                    <Text strong>Total</Text>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={1} align="right">
                                    <Text strong style={{fontSize:15}}>
                                      ${parseFloat(orgBillingData.total_amount).toFixed(2)}
                                    </Text>
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                              )}
                            />
                            {visibleLineItems.some(li => li.is_custom) && (
                              <Text type="secondary" style={{fontSize:12,marginTop:6,display:"block"}}>
                                * Custom negotiated rate applies for this organisation.
                              </Text>
                            )}
                          </div>

                          {/* Events table */}
                          {orgBillingData.events?.length > 0 && (
                            <div style={{marginBottom:16}}>
                              <Text strong style={{display:"block",marginBottom:8}}>Billing Events</Text>
                              <Table
                                size="small"
                                dataSource={orgBillingData.events.map((e,i)=>({...e,key:e.id??i}))}
                                pagination={{pageSize:10,hideOnSinglePage:true}}
                                columns={[
                                  {title:"Date",   dataIndex:"event_date",  key:"date",   width:100,
                                    render:v=>v?new Date(v).toLocaleDateString():"—"},
                                  {title:"Type",   dataIndex:"event_type",  key:"type",   width:150,
                                    render:t=>{
                                      const colors={REGISTRATION:"blue",PRESCRIPTION:"purple",DISPENSE:"green",
                                                   EYE_EXAMINATION:"cyan",LENS_PRESCRIPTION:"geekblue"};
                                      return <Tag color={colors[t]??"default"} style={{fontSize:11}}>{t.replace(/_/g," ")}</Tag>;
                                    }},
                                  {title:"User",   dataIndex:"user_email",  key:"user",   ellipsis:true},
                                  {title:"Amount", dataIndex:"amount",      key:"amount", width:100, align:"right",
                                    render:v=><Text strong>${parseFloat(v).toFixed(2)}</Text>},
                                ]}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Invoice History */}
                      <div>
                        <Text strong style={{display:"block",marginBottom:8,fontSize:14}}>Invoice History</Text>
                        <Table
                          size="small"
                          loading={orgInvoicesLoading}
                          dataSource={orgInvoices.map(inv=>({...inv,key:inv.id}))}
                          pagination={{pageSize:5,hideOnSinglePage:true}}
                          locale={{emptyText:"No invoices generated yet"}}
                          columns={[
                            {title:"Invoice #",  dataIndex:"invoice_number", key:"num",    ellipsis:true, width:170},
                            {title:"Period",     dataIndex:"period_label",   key:"period", width:110},
                            {title:"Modules",    dataIndex:"subscribed_modules", key:"modules",
                              render:mods=>(mods??[]).map(m=>(
                                <Tag key={m} color="geekblue" style={{fontSize:10,margin:"1px"}}>{m.charAt(0)+m.slice(1).toLowerCase()}</Tag>
                              ))},
                            {title:"Amount",     dataIndex:"total_amount",   key:"amount", width:100, align:"right",
                              render:v=><Text strong>${parseFloat(v).toFixed(2)}</Text>},
                            {title:"Status",     dataIndex:"status",         key:"status", width:85,
                              render:s=>{
                                const b=INVOICE_STATUS_BADGE[s]??{color:"default",text:s};
                                return <Tag color={b.color} style={{fontSize:11}}>{b.text}</Tag>;
                              }},
                            {title:"Due",        dataIndex:"due_date",       key:"due",    width:95,
                              render:v=>v?new Date(v).toLocaleDateString():"—"},
                            {title:"Actions",    key:"actions",              width:140,
                              render:(_,inv)=>(
                                <Space size={4}>
                                  <Button size="small" onClick={()=>{setPartnerInvDetail(inv);setInvoiceDetailOpen(true);}}>
                                    View
                                  </Button>
                                  {inv.status !== "PAID" && (
                                    <Popconfirm
                                      title="Mark this invoice as paid?"
                                      onConfirm={()=>handleMarkInvoicePaid(inv)}
                                      okText="Confirm Paid"
                                    >
                                      <Button size="small" type="primary">Mark Paid</Button>
                                    </Popconfirm>
                                  )}
                                </Space>
                              )},
                          ]}
                        />
                      </div>
                    </Spin>
                  );
                })(),
              },
              {
                key: "modules",
                label: "Modules",
                children: (() => {
                  const mods = orgDetailTarget?.subscribed_modules ?? [];
                  return (
                    <Spin spinning={orgModulesSaving}>
                      <Text type="secondary" style={{display:"block",marginBottom:16,fontSize:13}}>
                        Select which DHN modules this partner has access to. Users under this organisation
                        will only see subscribed modules. Billing events are only recorded for subscribed modules.
                      </Text>
                      <Row gutter={[12,12]}>
                        {MODULE_DEFS.map(({key,label,icon,description,billing}) => {
                          const active = mods.includes(key);
                          return (
                            <Col xs={24} md={12} lg={8} key={key}>
                              <Card
                                size="small"
                                style={{
                                  height:"100%",
                                  borderRadius:8,
                                  borderLeft: active ? "4px solid #52c41a" : "4px solid #d9d9d9",
                                  background: active ? "#fff" : "#fafafa",
                                  transition: "border-color 0.25s, background 0.25s",
                                }}
                              >
                                <Row justify="space-between" align="top" style={{marginBottom:8}}>
                                  <Space size={8}>
                                    {icon}
                                    <Text strong style={{fontSize:13}}>{label}</Text>
                                  </Space>
                                  {active ? (
                                    <Popconfirm
                                      title={`Disable ${label}?`}
                                      description={`Users of ${orgDetailTarget?.name} will immediately lose access to ${label} features.`}
                                      onConfirm={()=>handleToggleModule(key, false)}
                                      okText="Disable" okButtonProps={{danger:true}}
                                      cancelText="Cancel"
                                    >
                                      <Switch checked size="small" />
                                    </Popconfirm>
                                  ) : (
                                    <Switch
                                      checked={false}
                                      size="small"
                                      onChange={()=>handleToggleModule(key, true)}
                                    />
                                  )}
                                </Row>
                                <Text type="secondary" style={{display:"block",fontSize:12,marginBottom:6}}>{description}</Text>
                                <Text style={{display:"block",fontSize:11,color:"#1677ff"}}>{billing}</Text>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Spin>
                  );
                })(),
              },
            ]} />
          </div>
        ) : null}
      </Modal>

      {/* ── Invoice Detail Modal (Admin) ── */}
      {(() => {
        const inv = partnerInvDetail;
        const INVOICE_STATUS_BADGE = {
          DRAFT:   {color:"default", text:"Draft"},
          SENT:    {color:"blue",    text:"Sent"},
          VIEWED:  {color:"purple",  text:"Viewed"},
          PAID:    {color:"success", text:"Paid"},
          OVERDUE: {color:"error",   text:"Overdue"},
        };
        return (
          <Modal
            title={inv ? `Invoice ${inv.invoice_number}` : "Invoice Detail"}
            open={invoiceDetailOpen}
            onCancel={()=>{ setInvoiceDetailOpen(false); setPartnerInvDetail(null); }}
            footer={
              inv ? (
                <Space>
                  {inv.status !== "PAID" && (
                    <Popconfirm
                      title="Mark this invoice as paid?"
                      onConfirm={()=>handleMarkInvoicePaid(inv)}
                      okText="Confirm Paid"
                    >
                      <Button type="primary">Mark as Paid</Button>
                    </Popconfirm>
                  )}
                  <Button disabled title="PDF export — coming soon">Export as PDF</Button>
                  <Button onClick={()=>{ setInvoiceDetailOpen(false); setPartnerInvDetail(null); }}>Close</Button>
                </Space>
              ) : null
            }
            width={560}
            destroyOnHidden
          >
            {inv && (
              <>
                <Descriptions column={2} size="small" bordered style={{marginBottom:16}}>
                  <Descriptions.Item label="Organisation" span={2}>{inv.organisation_name}</Descriptions.Item>
                  <Descriptions.Item label="Period">{inv.period_label}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={INVOICE_STATUS_BADGE[inv.status]?.color ?? "default"}>
                      {INVOICE_STATUS_BADGE[inv.status]?.text ?? inv.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Sent">{fmtDate(inv.sent_at)}</Descriptions.Item>
                  <Descriptions.Item label="Due">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString("en-GB") : "—"}
                  </Descriptions.Item>
                  {inv.paid_at && (
                    <Descriptions.Item label="Paid" span={2}>{fmtDate(inv.paid_at)}</Descriptions.Item>
                  )}
                </Descriptions>
                <Table
                  size="small"
                  pagination={false}
                  style={{marginBottom:16}}
                  dataSource={[
                    {key:"reg",  label:"Patient Registration",    count:inv.total_registrations,      amount:inv.registration_amount},
                    {key:"rx",   label:"Prescription Issued",      count:inv.total_prescriptions,      amount:inv.prescription_amount},
                    {key:"disp", label:"Prescription Dispensed",   count:inv.total_dispenses,          amount:inv.dispense_amount},
                    {key:"eye",  label:"Eye Examination",          count:inv.total_eye_exams,          amount:inv.eye_exam_amount},
                    {key:"lens", label:"Lens Prescription Issued", count:inv.total_lens_prescriptions, amount:inv.lens_prescription_amount},
                  ]}
                  columns={[
                    {title:"Service", dataIndex:"label",  key:"label"},
                    {title:"Count",   dataIndex:"count",  key:"count",  width:70, align:"right"},
                    {title:"Amount",  dataIndex:"amount", key:"amount", width:110, align:"right",
                      render:v=><Text strong>${parseFloat(v).toFixed(2)}</Text>},
                  ]}
                  footer={()=>(
                    <Row justify="end">
                      <Col>
                        <Text strong style={{fontSize:16}}>
                          Total: ${parseFloat(inv.total_amount).toFixed(2)}
                        </Text>
                      </Col>
                    </Row>
                  )}
                />
              </>
            )}
          </Modal>
        );
      })()}

      {/* ── Reset Password Modal ── */}
      <Modal
        title={`Reset Password — ${resetPwTarget?.email ?? ""}`}
        open={Boolean(resetPwTarget)}
        onCancel={()=>{setResetPwTarget(null);setResetPwResult(null);setResetPwMode("generate");setResetPwSpecific("");setResetPwConfirm("");}}
        footer={
          resetPwResult ? (
            <Button onClick={()=>{setResetPwTarget(null);setResetPwResult(null);setResetPwMode("generate");setResetPwSpecific("");setResetPwConfirm("");}}>Close</Button>
          ) : (
            <Space>
              <Button onClick={()=>{setResetPwTarget(null);setResetPwResult(null);setResetPwMode("generate");setResetPwSpecific("");setResetPwConfirm("");}}>Cancel</Button>
              <Button type="primary" danger loading={resetPwLoading} onClick={handleResetPassword}>
                Reset Password
              </Button>
            </Space>
          )
        }
        destroyOnHidden
        width={480}
      >
        {resetPwResult ? (
          <div style={{textAlign:"center",padding:"8px 0"}}>
            <CheckCircleOutlined style={{fontSize:40,color:"#52c41a",marginBottom:12}} />
            <Text strong style={{display:"block",fontSize:15,marginBottom:16}}>Password Reset Successfully</Text>
            <div style={{
              background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:8,
              padding:"16px 20px",marginBottom:12,
            }}>
              <Text type="secondary" style={{display:"block",fontSize:12,marginBottom:8}}>New password:</Text>
              <Text code copyable style={{fontSize:20,fontWeight:700,letterSpacing:2}}>
                {resetPwResult}
              </Text>
            </div>
            <Alert type="warning" showIcon style={{textAlign:"left"}}
              message="Share this password securely with the user. It will not be shown again." />
          </div>
        ) : (
          <>
            <Alert type="warning" showIcon style={{marginBottom:16}}
              message={`This will immediately reset the password for ${resetPwTarget?.email}.`} />

            {/* Option selector */}
            <div style={{display:"flex",gap:10,marginBottom:16}}>
              <div
                onClick={()=>setResetPwMode("generate")}
                style={{
                  flex:1,border:`2px solid ${resetPwMode==="generate"?"#1677ff":"#E5E7EB"}`,
                  background:resetPwMode==="generate"?"#EFF6FF":"#fff",
                  borderRadius:8,padding:"12px 14px",cursor:"pointer",
                  transition:"border-color 0.15s,background-color 0.15s",
                }}>
                <Text strong style={{display:"block",fontSize:13,color:resetPwMode==="generate"?"#1677ff":"#111827"}}>
                  Generate Password
                </Text>
                <Text style={{fontSize:11,color:"#6B7280"}}>A strong random password will be created</Text>
              </div>
              <div
                onClick={()=>setResetPwMode("specific")}
                style={{
                  flex:1,border:`2px solid ${resetPwMode==="specific"?"#1677ff":"#E5E7EB"}`,
                  background:resetPwMode==="specific"?"#EFF6FF":"#fff",
                  borderRadius:8,padding:"12px 14px",cursor:"pointer",
                  transition:"border-color 0.15s,background-color 0.15s",
                }}>
                <Text strong style={{display:"block",fontSize:13,color:resetPwMode==="specific"?"#1677ff":"#111827"}}>
                  Set Specific Password
                </Text>
                <Text style={{fontSize:11,color:"#6B7280"}}>Enter the password you want to set</Text>
              </div>
            </div>

            {resetPwMode==="specific" && (
              <>
                <div style={{marginBottom:12}}>
                  <Text style={{display:"block",fontSize:13,marginBottom:6,fontWeight:500}}>New Password</Text>
                  <Input.Password
                    placeholder="At least 8 characters"
                    value={resetPwSpecific}
                    onChange={e=>setResetPwSpecific(e.target.value)}
                    style={{marginBottom:8}}
                  />
                  <Text style={{display:"block",fontSize:13,marginBottom:6,fontWeight:500}}>Confirm Password</Text>
                  <Input.Password
                    placeholder="Repeat the password"
                    value={resetPwConfirm}
                    onChange={e=>setResetPwConfirm(e.target.value)}
                    status={resetPwConfirm && resetPwSpecific!==resetPwConfirm?"error":undefined}
                  />
                  {resetPwConfirm && resetPwSpecific!==resetPwConfirm && (
                    <Text type="danger" style={{fontSize:11}}>Passwords do not match</Text>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* ── Reassign Organisation Modal ── */}
      <Modal
        title={`Reassign Organisation — ${reassignOrgTarget?.email ?? ""}`}
        open={reassignOrgOpen}
        onOk={handleReassignOrg}
        onCancel={()=>{setReassignOrgOpen(false);setReassignOrgTarget(null);setReassignOrgId(null);}}
        okText="Reassign"
        confirmLoading={submitting}
        destroyOnHidden
      >
        <div style={{marginBottom:16}}>
          <Text type="secondary">Current organisation: </Text>
          <Text strong>{reassignOrgTarget?.organisation_name ?? "None"}</Text>
        </div>
        <Select
          style={{width:"100%"}}
          placeholder="Select new organisation"
          value={reassignOrgId}
          onChange={setReassignOrgId}
        >
          {orgRows.map(o=>(
            <Option key={o.id} value={o.id}>
              {o.name}{o.is_owner?" (Owner — MCHUSA Direct)":""}
            </Option>
          ))}
        </Select>
      </Modal>

    </Layout>
  );
}
